import React, { useEffect, useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { playErrorSound, playSuccessSound } from '@/lib/errorSound';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Send, Trash2, User, Loader2, Clock, Edit2, Check, X, Zap, Info, Copy, Download, FileText, CornerDownRight, Square, Lock, Pencil, Brain, ChevronDown, ChevronLeft, Plus, ArrowUp, ArrowDown, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { FormattedMessage } from '@/components/ui/formatted-message';
import { UserVersions } from '@/components/chat/UserVersions';
import { AssistantVersions } from '@/components/chat/AssistantVersions';
import { useConversationTree } from '@/hooks/useConversationTree';
import { Header } from '@/components/Header';
import { determineMessageKind } from '@/lib/message-kind';
import { trackEvent } from '@/lib/analytics';
import { getAssistantLensId, resolveRegenerationLensId } from '@/lib/chat-lens-routing';
import { QuestionMessage } from '@/components/chat/QuestionMessage';
import { PdfDocumentViewer } from '@/components/documents/PdfDocumentViewer';
import { useDocumentPdf } from '@/hooks/useDocumentPdf';
import { useCredits } from '@/hooks/useCredits';
import { ToastAction } from '@/components/ui/toast';
import { ConvertPrompt } from '@/components/conversion/ConvertPrompt';
import { FirstMessageNotice } from '@/components/chat/FirstMessageNotice';
import { ArtifactOperatingSystemAction } from '@/components/operating-systems/ArtifactOperatingSystemAction';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  MAX_CONVERSATION_METHODS,
  buildConversationMethodsPayload,
  buildOptionalConversationMethodsPayload,
  getConversationBoardName,
  getConversationSurfaceRedirect,
  resolveConversationMethodOrder,
  uniqueConversationMethodIds,
} from '@/lib/conversation-methods';
import {
  CONTEXT_DOCUMENT_BUCKET,
  CONTEXT_DOCUMENT_ACCEPT,
  CONTEXT_DOCUMENT_QUOTA_MESSAGE,
  MAX_ACTIVE_CONTEXT_DOCUMENTS,
  MAX_CONTEXT_DOCUMENT_FILE_BYTES,
  countReservedContextDocumentSlots,
  extractContextDocumentText,
  getContextDocumentUploadErrorMessage,
  getSupportedContextDocumentKind,
  isServerNormalizedContextDocument,
  mergeContextDocumentLists,
  processContextDocumentFiles,
  replaceContextDocument,
  resolveContextDocumentUpload,
  sanitizeContextDocumentFileName,
  sha256ContextDocumentBytes,
  normalizeContextDocumentName,
} from '@/lib/conversation-context-documents';
import { createUuid } from '@/lib/uuid';
import { handleConversationLoadFailure, throwIfConversationLoadError } from '@/lib/conversation-load-failure';

const DOCUMENT_CREDIT_THRESHOLD = 10;
const DOCUMENT_LIMIT = 1;
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getIndustryMenuLabel, groupByIndustry } from '@/lib/industries';

class ConversationNotFoundError extends Error {
  constructor() {
    super('Conversation not found');
    this.name = 'ConversationNotFoundError';
  }
}

interface ThinkingMethod {
  id: string;
  name: string;
  author: string;
  bio: string;
  icon_url?: string;
  status?: string;
  author_email?: string | null;
  industry?: string;
  header?: string;
  teaser?: string;
  skills?: string[];
}

type LensOption = ThinkingMethod & {
  status: string;
};

interface Message {
  id: string;
  conversation_id?: string;
  content: string;
  is_user: boolean;
  created_at: string;
  kind?: "CHAT" | "ARTF" | "QUESTION" | "ANSWER" | "EDIT";
  model?: string;
  tokens?: number;
  is_edited?: boolean;
  is_regenerated?: boolean;
  retrieval_latency_ms?: number;
  model_latency_ms?: number;
  retrieval_hit_count?: number;
  parent_message_id?: string;
  client_message_id?: string | null;
  thinking_method_id?: string | null;
  properties?: Record<string, unknown> | null;
  user_id?: string | null;
  version?: number;
}

interface Conversation {
  id: string;
  thinking_method_id?: string;
  openai_conversation_id?: string | null;
  // When true, this conversation has not been persisted to the DB yet.
  // It will be created lazily the moment the user sends their first message,
  // so we don't pollute "Recent Conversations" with empty greeting-only rows.
  pending?: boolean;
}

interface ContextDocument {
  id: string;
  storage_path?: string;
  file_name: string;
  content_sha256?: string | null;
  content_type?: string | null;
  file_size: number;
  active: boolean;
  status: string;
  error?: string | null;
  created_at: string;
  updated_at?: string;
  replacementUploading?: boolean;
}

type NextMoveActionType = 'generic_question' | 'create_artifact' | 'other';

interface NextMoveActionItem {
  type: NextMoveActionType;
  title: string;
  description?: string;
  question?: string;
  raw: string;
}

interface ParsedAssistantContent {
  content: string;
  actionItems: NextMoveActionItem[];
}

interface PdfGenerationResult {
  success?: boolean;
  followUpMessageId?: string | null;
  followUpStatus?: string | null;
  followUpReason?: string | null;
  followUpError?: string | null;
}

type ConferenceStreamResult = {
  index: number;
  count: number;
  thinkingMethodId?: string;
  lensName?: string;
  lensAuthor?: string | null;
  content: string;
  status: 'thinking' | 'done' | 'error';
  messageId?: string | null;
  kind?: string | null;
  error?: string | null;
};

// Marker for the synthetic, in-memory greeting message that exists only on the client
// before the conversation is persisted. We never send this id to the backend.
const LOCAL_GREETING_ID_PREFIX = 'local-greeting-';
const isLocalGreetingId = (id: string | undefined | null) =>
  !!id && id.startsWith(LOCAL_GREETING_ID_PREFIX);
const MAX_CONFERENCE_LENSES = MAX_CONVERSATION_METHODS;

const buildInitialBoardGreetingMessages = async ({
  conversationId,
  userId,
  methodIds,
  lenses,
}: {
  conversationId: string;
  userId: string;
  methodIds: string[];
  lenses: Array<Pick<ThinkingMethod, 'id' | 'name' | 'author'>>;
}): Promise<Message[]> => {
  const orderedIds = uniqueConversationMethodIds(methodIds);
  if (orderedIds.length === 0) return [];

  const { data: greetingAttrs, error: greetingError } = await supabase
    .from('thinking_method_attributes')
    .select('thinking_method_id, attribute_value')
    .in('thinking_method_id', orderedIds)
    .eq('attribute_name', 'GREETING');

  throwIfConversationLoadError(greetingError, 'thinking_method_attributes', 'select greeting');

  const greetingByLensId = new Map(
    (greetingAttrs || [])
      .filter((attr) => typeof attr.attribute_value === 'string' && attr.attribute_value.trim().length > 0)
      .map((attr) => [attr.thinking_method_id, attr.attribute_value.trim()]),
  );
  const lensById = new Map(lenses.map((lens) => [lens.id, lens]));
  const rowIds = orderedIds.map(() => createUuid());
  const createdAtBase = Date.now();
  const count = orderedIds.length;

  const rows = orderedIds.map((lensId, index) => {
    const lens = lensById.get(lensId);
    const lensName = lens?.name || `Lens ${index + 1}`;
    const lensAuthor = lens?.author || '';
    const fallbackGreeting = index === 0
      ? `You are now using ${lensName}.`
      : `You are now also using ${lensName}.`;

    return {
      id: rowIds[index],
      conversation_id: conversationId,
      parent_message_id: index === 0 ? null : rowIds[index - 1],
      version: 0,
      content: greetingByLensId.get(lensId) || fallbackGreeting,
      is_user: false,
      thinking_method_id: lensId,
      kind: 'CHAT',
      user_id: userId,
      created_at: new Date(createdAtBase + index).toISOString(),
      properties: {
        avoid_context: true,
        conference_lens: {
          v: 1,
          name: lensName,
          ...(lensAuthor ? { author: lensAuthor } : {}),
          index: index + 1,
          count,
        },
      },
    };
  });

  const { data: insertedMessages, error: insertError } = await supabase
    .from('messages_vr')
    .insert(rows)
    .select('*');

  if (insertError) throw insertError;

  return ((insertedMessages || rows) as Message[])
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};

type LensVisibilityProfile = {
  is_admin?: boolean | null;
  email?: string | null;
};

const isVisibleLensForProfile = (
  method: Pick<ThinkingMethod, 'status' | 'author_email'>,
  profile: LensVisibilityProfile | null,
) => {
  const isAdmin = profile?.is_admin === true;
  const userEmail = (profile?.email || '').toLowerCase();
  const authorEmail = (method.author_email || '').toLowerCase();

  switch (method.status) {
    case 'draft':
      return false;
    case 'admins':
      return isAdmin;
    case 'author':
      return isAdmin || (!!authorEmail && authorEmail === userEmail);
    case 'published':
      return true;
    default:
      return false;
  }
};

const mergeLensProperties = (
  methods: ThinkingMethod[],
  attrs: { thinking_method_id: string; attribute_value: string }[] | null | undefined,
): LensOption[] => {
  const propsMap: Record<string, Pick<LensOption, 'industry' | 'header' | 'teaser' | 'skills'>> = {};
  for (const attr of attrs || []) {
    try {
      const parsed = JSON.parse(attr.attribute_value);
      propsMap[attr.thinking_method_id] = {
        industry: parsed.industry || undefined,
        header: parsed.header || undefined,
        teaser: parsed.teaser || undefined,
        skills: Array.isArray(parsed.skills) ? parsed.skills : undefined,
      };
    } catch { /* Optional cleanup must not interrupt the primary action. */ }
  }

  return methods.map((method) => ({
    ...method,
    status: method.status || 'draft',
    ...propsMap[method.id],
  }));
};

const cleanupActionText = (text?: string | null): string | undefined => {
  if (!text) return undefined;
  const cleaned = text
    .replace(/\*\*/g, '')
    .replace(/^[_*]+/, '')
    .replace(/[_*]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || undefined;
};

const extractNextMoveActionItems = (content: string): ParsedAssistantContent => {
  if (!content) {
    return { content: '', actionItems: [] };
  }

  const lines = content.split('\n');
  const nextMoveIndex = lines.findIndex((line) => line.trim().toLowerCase().startsWith('next move'));

  if (nextMoveIndex === -1) {
    return { content, actionItems: [] };
  }

  const contentPart = lines.slice(0, nextMoveIndex).join('\n').trimEnd();
  const actionPart = lines.slice(nextMoveIndex + 1).join('\n').trim();

  if (!actionPart) {
    return { content: contentPart, actionItems: [] };
  }

  const normalizedActionPart = actionPart.replace(/\r/g, '');
  const items: NextMoveActionItem[] = [];
  const itemRegex = /(?:^|\n)\s*\d+[.)]?\s+([\s\S]*?)(?=(?:\n\s*\d+[.)]|\n*$))/g;

  let match: RegExpExecArray | null;
  while ((match = itemRegex.exec(normalizedActionPart)) !== null) {
    const rawItem = match[1].trim();
    if (!rawItem) continue;

    const numberedStripped = rawItem.replace(/^\d+[.)]?\s*/, '').trim();
    if (!numberedStripped) continue;

    let type: NextMoveActionType = 'other';
    let remainder = numberedStripped;

    const typeMatch = remainder.match(/^(create artifact|generic question)\s*(?:[-—–:]\s*)?(.*)$/i);
    if (typeMatch) {
      const prefix = typeMatch[1].toLowerCase();
      remainder = typeMatch[2].trim();
      if (prefix === 'create artifact') {
        type = 'create_artifact';
      } else if (prefix === 'generic question') {
        type = 'generic_question';
      }
    }

    let title = remainder;
    let description: string | undefined;

    const colonIndex = remainder.indexOf(':');
    if (colonIndex !== -1) {
      title = remainder.slice(0, colonIndex).trim();
      description = remainder.slice(colonIndex + 1).trim();
    }

    if (!title) {
      title = description || rawItem;
    }

    title = cleanupActionText(title) || '';
    description = cleanupActionText(description);

    const question =
      type === 'generic_question'
        ? cleanupActionText(description && description.length ? description : remainder)
        : undefined;

    const cleanedRaw = cleanupActionText(numberedStripped) || '';

    items.push({
      type,
      title,
      description,
      question,
      raw: cleanedRaw,
    });
  }

  if (items.length === 0) {
    const fallbackLines = normalizedActionPart
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    fallbackLines.forEach((rawItem) => {
      const numberedStripped = rawItem.replace(/^\d+[.)]?\s*/, '').trim();
      if (!numberedStripped) return;

      let type: NextMoveActionType = 'other';
      let remainder = numberedStripped;

      const typeMatch = remainder.match(/^(create artifact|generic question)\s*(?:[-—–:]\s*)?(.*)$/i);
      if (typeMatch) {
        const prefix = typeMatch[1].toLowerCase();
        remainder = typeMatch[2].trim();
        if (prefix === 'create artifact') {
          type = 'create_artifact';
        } else if (prefix === 'generic question') {
          type = 'generic_question';
        }
      }

      let title = remainder;
      let description: string | undefined;
      const colonIndex = remainder.indexOf(':');
      if (colonIndex !== -1) {
        title = remainder.slice(0, colonIndex).trim();
        description = remainder.slice(colonIndex + 1).trim();
      }
      if (!title) {
        title = description || rawItem;
      }

      title = cleanupActionText(title) || '';
      description = cleanupActionText(description);
      const question =
        type === 'generic_question'
          ? cleanupActionText(description && description.length ? description : remainder)
          : undefined;

      items.push({
        type,
        title,
        description,
        question,
        raw: cleanupActionText(numberedStripped) || '',
      });
    });
  }

  return {
    content: contentPart,
    actionItems: items,
  };
};

// Helper to trigger PDF generation via edge function
const triggerPdfGeneration = async (messageId: string, userId?: string): Promise<PdfGenerationResult | null> => {
  if (!messageId || !userId) {
    console.warn('[PDF] Missing messageId or userId for PDF generation');
    return null;
  }
  
  try {
    console.log(`[PDF] Triggering generation for message: ${messageId}`);
    const { data, error } = await supabase.functions.invoke('generate-pdf', {
      body: { messageId, userId }
    });
    
    if (error) {
      console.error('[PDF] Generation failed:', error);
      return null;
    } else {
      console.log('[PDF] Generation triggered successfully:', data);
      return (data ?? null) as PdfGenerationResult | null;
    }
  } catch (err) {
    console.error('[PDF] Trigger failed:', err);
    return null;
  }
};

type ChatProps = {
  boardMode?: boolean;
};

const Chat = ({ boardMode = false }: ChatProps) => {
  const { conversationId: routeConversationId } = useParams<{ conversationId: string }>();
  const { user, refreshSession, isAnonymous, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedConversationId = searchParams.get('conversation');
  const requestedMethodId = searchParams.get('method');
  const hasChatTarget = !!requestedConversationId || !!requestedMethodId || (!!routeConversationId && routeConversationId !== 'new');
  const fromApplications = ['applications', 'operating-systems'].includes(searchParams.get('from') ?? '');
  const applicationsOriginSearch = fromApplications ? '?from=applications' : '';
  const { toast } = useToast();
  const {
    credits: creditsBalance,
    documentsCreated,
    loading: creditsLoading,
    refresh: refreshCredits,
  } = useCredits();
  const [thinkingMethod, setThinkingMethod] = useState<ThinkingMethod | null>(null);
  const [availableLenses, setAvailableLenses] = useState<LensOption[]>([]);
  const [lensNameById, setLensNameById] = useState<Record<string, string>>({});
  const [lensPickerOpen, setLensPickerOpen] = useState(false);
  const [boardDraftLensIds, setBoardDraftLensIds] = useState<string[]>([]);
  const [selectedLensIndustry, setSelectedLensIndustry] = useState<string | null>(null);
  const [switchingLens, setSwitchingLens] = useState(false);
  const [selectedLensIds, setSelectedLensIds] = useState<string[]>([]);
  const [documentLensId, setDocumentLensId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deepThinking, setDeepThinking] = useState(false);
  const [useDeepThinkingMode, setUseDeepThinkingMode] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState<{
    parentMessageId: string;
    output: string;
    reasoning: string;
    isDeepThinking: boolean;
    conferenceResults?: ConferenceStreamResult[];
  } | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editDeepThinkingMode, setEditDeepThinkingMode] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [mobilePanel, setMobilePanel] = useState<'chat' | 'document'>('chat');
  const [convertPromptOpen, setConvertPromptOpen] = useState(false);
  const [convertPromptReason, setConvertPromptReason] = useState<string | undefined>(undefined);
  const [convertPromptTitle, setConvertPromptTitle] = useState<string | undefined>(undefined);
  const [contextDocuments, setContextDocuments] = useState<ContextDocument[]>([]);
  const contextDocumentsRef = useRef<ContextDocument[]>([]);
  const updateContextDocuments = (update: React.SetStateAction<ContextDocument[]>) => {
    const next = typeof update === 'function' ? update(contextDocumentsRef.current) : update;
    contextDocumentsRef.current = next;
    setContextDocuments(next);
  };
  const [contextDocumentUploading, setContextDocumentUploading] = useState(false);
  const [sourceDocumentsOpen, setSourceDocumentsOpen] = useState(false);
  const [renamingBoard, setRenamingBoard] = useState(false);
  const [boardNameDraft, setBoardNameDraft] = useState('');
  const [boardCreationNameDraft, setBoardCreationNameDraft] = useState('');
  const [savingBoardName, setSavingBoardName] = useState(false);
  const [guestSessionError, setGuestSessionError] = useState('');
  const [guestSessionAttempt, setGuestSessionAttempt] = useState(0);
  const openConvertPrompt = (reason?: string, title?: string) => {
    setConvertPromptReason(reason);
    setConvertPromptTitle(title);
    setConvertPromptOpen(true);
  };

  const trackCreditsRequestNavigation = (source: string) => {
    trackEvent('request_more_credits_clicked', {
      source,
      page_path: location.pathname,
      status: 'clicked',
    });
    navigate('/account');
  };

  const handleCopyMessage = async (message: Message) => {
    if (isAnonymous) {
      openConvertPrompt();
      return;
    }
    const text = typeof message.content === 'string'
      ? message.content
      : JSON.stringify(message.content);
    try {
      // Render markdown to HTML so pasting into rich-text editors (Gmail, Docs, Notion)
      // preserves formatting — same UX as ChatGPT.
      const { marked } = await import('marked');
      const html = await marked.parse(text, { breaks: true, gfm: true });
      const wrappedHtml = `<div>${html}</div>`;

      let copied = false;
      if (typeof window !== 'undefined' && window.ClipboardItem && navigator.clipboard?.write) {
        try {
          const item = new ClipboardItem({
            'text/html': new Blob([wrappedHtml], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          });
          await navigator.clipboard.write([item]);
          copied = true;
        } catch (richErr) {
          console.warn('Rich clipboard write failed, falling back to plain text:', richErr);
        }
      }
      if (!copied) {
        await navigator.clipboard.writeText(text);
      }

      setCopiedMessageId(message.id);
      setTimeout(() => setCopiedMessageId((prev) => (prev === message.id ? null : prev)), 2000);
      trackEvent('copy_clicked', {
        source: 'chat_message',
        message_kind: message.kind || 'unknown',
        status: 'success',
      });
      toast({ title: 'Copied', description: 'Message copied with formatting' });
    } catch (err) {
      console.error('Failed to copy message:', err);
      toast({ title: 'Copy failed', description: 'Unable to copy message', variant: 'destructive' });
    }
  };
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const contextDocumentInputRef = useRef<HTMLInputElement>(null);
  const contextDocumentUploadBatchesRef = useRef(0);
  const contextDocumentQueueRef = useRef<Promise<void>>(Promise.resolve());
  const recentlyUploadedContextDocumentsRef = useRef(new Map<string, { conversationId: string; document: ContextDocument }>());
  const artifactScrollRef = useRef<HTMLDivElement>(null);
  const previousArtifactIdsRef = useRef<string[]>([]);
  const artifactsInitializedRef = useRef(false);
  const viewedArtifactIdsRef = useRef<Set<string>>(new Set());
  const autoScrollEnabledRef = useRef(true);
  const lastMessageIdRef = useRef<string | null>(null);
  const [cancelledClientMessageIds, setCancelledClientMessageIds] = useState<string[]>([]);
  const cancelledClientMessageIdsRef = useRef<Set<string>>(new Set());
  const activeClientMessageIdRef = useRef<string | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const switchingLensRef = useRef(false);
  const suppressNextRouteFetchRef = useRef<string | null>(null);
  const guestSessionRequestInFlightRef = useRef(false);
  const [investigationInProgress, setInvestigationInProgress] = useState(false);
  const [investigationQuestionId, setInvestigationQuestionId] = useState<string | null>(null);
  const [editingArtifactId, setEditingArtifactId] = useState<string | null>(null);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [documentInfo, setDocumentInfo] = useState<{
    messageId: string;
    title: string;
    fileSize?: number;
  } | null>(null);
  const activeDocumentInfo = documentInfo?.messageId === selectedArtifactId ? documentInfo : null;
  const handleDocumentLoad = useCallback((info: { title: string; fileSize?: number }) => {
    if (!selectedArtifactId) return;
    setDocumentInfo({
      messageId: selectedArtifactId,
      ...info,
    });
  }, [selectedArtifactId]);
  
  // Conversation tree: computes active path from message tree
  const { activePath, selectBranch, getVersionInfoForMessage, treeIndex } = useConversationTree(messages);

  // Reset document info when artifact changes
  useEffect(() => {
    setDocumentInfo(null);
  }, [selectedArtifactId]);

  // Get PDF document info for downloads (pass userId to trigger auto-generation)
  const {
    documentPdf,
    generating: pdfGenerating,
    waitingForDocument: pdfWaitingForDocument,
  } = useDocumentPdf(selectedArtifactId, user?.id);
  const pdfPreparing = pdfGenerating || (!!user?.id && pdfWaitingForDocument);

  const documentLimitReached = isAnonymous && !creditsLoading && documentsCreated >= DOCUMENT_LIMIT;
  const documentSwitchLocked =
    documentLimitReached ||
    (!isAnonymous && !creditsLoading && creditsBalance < DOCUMENT_CREDIT_THRESHOLD);
  const redirectAnonymousDocumentCreation = () => {
    navigate('/beta_access');
  };
  const promptDocumentUpgrade = () => {
    if (documentLimitReached) {
      trackEvent('credits_exhausted', {
        source: 'document_mode',
        status: 'document_limit_reached',
      });
      toast({
        title: 'Free preview document limit reached',
        description: 'Create an account to keep creating documents.',
        variant: 'destructive',
      });
      return;
    }

    if (isAnonymous) {
      openConvertPrompt();
      return;
    }

    trackEvent('request_more_credits_clicked', {
      source: 'document_mode_gate',
      page_path: location.pathname,
      status: 'clicked',
    });
    trackEvent('credits_exhausted', {
      source: 'document_mode',
      status: 'insufficient_credits',
    });
    toast({
      title: 'Not enough credits to create an App’s Document',
      description: `Document mode needs at least ${DOCUMENT_CREDIT_THRESHOLD} credits (you have ${creditsBalance}). Request more credits to unlock it.`,
      action: (
        <ToastAction altText="Request more credits" onClick={() => trackCreditsRequestNavigation('document_mode_toast')}>
          Request more credits
        </ToastAction>
      ),
    });
  };

  useEffect(() => {
    if (!documentLimitReached) return;
    setUseDeepThinkingMode(false);
    setEditDeepThinkingMode(false);
    setEditingArtifactId(null);
  }, [documentLimitReached]);
  
  // Ref to always hold the latest conversation to avoid stale closures in async callbacks
  const conversationRef = useRef<Conversation | null>(null);
  
  // Keep conversationRef in sync with conversation state
  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  // Auto-enable "Create Artifact" switch when clarification is in progress
  // When investigation starts, auto-enable document mode (but user can unlock it)
  useEffect(() => {
    if (investigationInProgress) {
      setUseDeepThinkingMode(true);
    }
  }, [investigationInProgress]);

  const markMessageCancelled = useCallback((clientMessageId?: string | null) => {
    if (!clientMessageId) return;
    setCancelledClientMessageIds((prev) =>
      prev.includes(clientMessageId) ? prev : [...prev, clientMessageId]
    );
    cancelledClientMessageIdsRef.current.add(clientMessageId);
  }, []);

  const isClientMessageCancelled = useCallback(
    (clientMessageId?: string | null) =>
      !!clientMessageId && cancelledClientMessageIdsRef.current.has(clientMessageId),
    []
  );

  const pruneCancelledMessages = useCallback((currentMessages: Message[] = []) => {
    const activeIds = new Set(
      currentMessages
        .map((m) => m.client_message_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );

    setCancelledClientMessageIds((prev) => {
      const next = prev.filter((id) => activeIds.has(id));
      cancelledClientMessageIdsRef.current = new Set(next);
      return next;
    });
  }, []);

  const clearCancelledMessages = useCallback(() => {
    setCancelledClientMessageIds([]);
    cancelledClientMessageIdsRef.current = new Set();
  }, []);

  const pendingResponseMessage = useMemo(() => {
    if (!messages.length) return null;

    const cancelledSet = new Set(cancelledClientMessageIds);
    // If user message is older than 5 minutes, the edge function has
    // either finished (and failed to persist) or timed out — stop showing
    // "Thinking..." so the user isn't stuck on a stale loading state.
    const STALE_THRESHOLD_MS = 5 * 60 * 1000;
    const now = Date.now();

    const sortedMessages = [...messages].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    for (let i = sortedMessages.length - 1; i >= 0; i--) {
      const msg = sortedMessages[i];
      if (!msg.is_user) continue;

      if (msg.client_message_id && cancelledSet.has(msg.client_message_id)) {
        continue;
      }

      const hasNewerUserChild = sortedMessages.some(
        (child) => child.is_user && child.parent_message_id === msg.id
      );
      if (hasNewerUserChild) {
        continue;
      }

      const hasAssistantChild = sortedMessages.some(
        (child) => !child.is_user && child.parent_message_id === msg.id
      );

      if (!hasAssistantChild) {
        const messageAge = now - new Date(msg.created_at).getTime();
        if (messageAge > STALE_THRESHOLD_MS) {
          // Message is stale — edge function likely timed out
          return null;
        }
        return msg;
      }

      break;
    }

    return null;
  }, [messages, cancelledClientMessageIds]);

  const hasPendingResponse = !!pendingResponseMessage;

  // Use user?.id as dependency instead of user object to avoid re-running
  // when Supabase refreshes the auth token on window focus (which creates a
  // new user object reference but same user id).
  const userId = user?.id;
  useEffect(() => {
    if (!hasChatTarget) {
      navigate('/lenses');
      return;
    }
    if (authLoading) return;
    if (!userId) {
      if (guestSessionRequestInFlightRef.current) return;
      guestSessionRequestInFlightRef.current = true;
      setGuestSessionError('');
      setLoading(true);
      supabase.auth.signInAnonymously()
        .then(({ error }) => {
          if (error) throw error;
        })
        .catch((error) => {
          console.error('Unable to start guest chat session:', error);
          guestSessionRequestInFlightRef.current = false;
          setGuestSessionError(error instanceof Error ? error.message : 'Unable to start a guest chat session.');
          setLoading(false);
        });
      return;
    }

    guestSessionRequestInFlightRef.current = false;

    if (
      routeConversationId &&
      routeConversationId !== 'new' &&
      suppressNextRouteFetchRef.current === routeConversationId
    ) {
      suppressNextRouteFetchRef.current = null;
      return;
    }

    fetchData();
  }, [authLoading, userId, hasChatTarget, routeConversationId, requestedConversationId, requestedMethodId, boardMode, navigate, guestSessionAttempt]);

  useLayoutEffect(() => {
    if (loading) return;

    const lastMessage = activePath.length ? activePath[activePath.length - 1] : null;
    const hasNewLastMessage = lastMessage?.id && lastMessage.id !== lastMessageIdRef.current;
    const shouldScroll =
      autoScrollEnabledRef.current || (hasNewLastMessage && lastMessage?.is_user);

    if (shouldScroll) {
      scrollToBottom(hasNewLastMessage ? 'smooth' : 'auto');
    }

    lastMessageIdRef.current = lastMessage?.id ?? null;
  }, [activePath, loading]);

  // Scroll to top when artifact changes
  useEffect(() => {
    setMobilePanel(selectedArtifactId ? 'document' : 'chat');
    if (artifactScrollRef.current) {
      artifactScrollRef.current.scrollTop = 0;
    }
  }, [selectedArtifactId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [newMessage]);

  useEffect(() => {
    if (editTextareaRef.current) {
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = Math.min(editTextareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [editContent]);

  const streamingConferenceProgress = streamingResponse?.conferenceResults
    ?.map((result) => `${result.index}:${result.status}:${result.content.length}`)
    .join('|') ?? '';

  useEffect(() => {
    if (streamingResponse) {
      scrollToBottom('smooth');
    }
  }, [streamingResponse?.output, streamingResponse?.reasoning, streamingConferenceProgress]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (container && container.getClientRects().length > 0) {
      const scroll = () => {
        if (container.getClientRects().length === 0) return;
        const target = container.scrollHeight;
        container.scrollTop = target;
        try {
          container.scrollTo({ top: target, behavior });
        } catch {
          container.scrollTop = target;
        }
        autoScrollEnabledRef.current = true;
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(scroll);
      } else {
        scroll();
      }
      return;
    }
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    autoScrollEnabledRef.current = distanceFromBottom < 120;
  };

  const loadVisibleLensOptions = async (): Promise<LensOption[]> => {
    const { data: methodsData, error: methodsError } = await supabase
      .from('thinking_methods')
      .select('*')
      .order('order_index', { ascending: true });
    throwIfConversationLoadError(methodsError, 'thinking_methods', 'list visible methods');

    const { data: profileData, error: profileError } = userId
      ? await supabase
          .from('profiles')
          .select('is_admin, email')
          .eq('user_id', userId)
          .maybeSingle()
      : { data: null, error: null };
    if (profileError?.code !== 'PGRST116') throwIfConversationLoadError(profileError, 'profiles', 'load profile');

    const filteredMethods = ((methodsData || []) as ThinkingMethod[])
      .filter((method) => isVisibleLensForProfile(method, profileData));
    const methodIds = filteredMethods.map((method) => method.id);
    const { data: attrsData, error: attrsError } = methodIds.length > 0
      ? await supabase
          .from('thinking_method_attributes')
          .select('thinking_method_id, attribute_value')
          .in('thinking_method_id', methodIds)
          .eq('attribute_name', 'JSON_PROPERTIES')
      : { data: [], error: null };
    throwIfConversationLoadError(attrsError, 'thinking_method_attributes', 'load method properties');

    const lenses = mergeLensProperties(filteredMethods, attrsData);
    setAvailableLenses(lenses);
    setLensNameById((prev) => ({
      ...prev,
      ...Object.fromEntries(lenses.map((lens) => [lens.id, lens.name])),
    }));
    return lenses;
  };

  const fetchData = async () => {
    if (!thinkingMethod && messages.length === 0) {
      setLoading(true);
    }
    try {
      const visibleLenses = await loadVisibleLensOptions();
      const visibleLensIds = new Set(visibleLenses.map((lens) => lens.id));
      let conversationData = null;
      let resolvedMethodId: string | null = requestedMethodId;
      const canonicalConversationId =
        requestedConversationId ||
        (routeConversationId && routeConversationId !== 'new' ? routeConversationId : null);

      if (canonicalConversationId) {
        const { data, error } = await supabase
          .from('conversations')
          .select('*')
          .eq('user_id', userId)
          .eq('id', canonicalConversationId)
          .maybeSingle();

        throwIfConversationLoadError(error, 'conversations', 'load conversation');

        if (data) {
          const redirect = getConversationSurfaceRedirect({
            conversationId: data.id,
            openaiConversationId: data.openai_conversation_id,
            currentSurface: boardMode ? 'board' : 'chat',
          });
          if (redirect) {
            navigate(`${redirect}${applicationsOriginSearch}`, { replace: true });
            setLoading(false);
            return;
          }

          conversationData = data;
          resolvedMethodId = data.thinking_method_id;
          if (requestedConversationId) {
            navigate(`${boardMode ? '/board' : '/chat'}/${data.id}${applicationsOriginSearch}`, { replace: true });
          }
        } else if (!requestedConversationId && routeConversationId && visibleLensIds.has(routeConversationId)) {
          resolvedMethodId = routeConversationId;
          navigate(`/chat/new?method=${routeConversationId}`, { replace: true });
        } else {
          throw new ConversationNotFoundError();
        }
      }

      if (!conversationData) {
        if (!resolvedMethodId && routeConversationId && routeConversationId !== 'new') {
          resolvedMethodId = routeConversationId;
        }
        if (!resolvedMethodId) {
          navigate('/lenses');
          return;
        }
        if (!visibleLensIds.has(resolvedMethodId)) {
          throw new Error('Lens is not available');
        }

        conversationData =
          conversationRef.current?.pending && conversationRef.current.thinking_method_id === resolvedMethodId
            ? conversationRef.current
            : ({
                id: '',
                thinking_method_id: resolvedMethodId,
                pending: true,
              } as Conversation);
      }

      resolvedMethodId = conversationData.thinking_method_id || resolvedMethodId;
      if (!resolvedMethodId) throw new Error('Conversation is missing a lens');
      const conversationMethodOrder = uniqueConversationMethodIds(
        resolveConversationMethodOrder(conversationData.openai_conversation_id, resolvedMethodId)
      );
      if (conversationMethodOrder.length > 0) {
        resolvedMethodId = conversationMethodOrder[0];
      }

      const { data: methodData, error: methodError } = await supabase
        .from('thinking_methods')
        .select('*')
        .eq('id', resolvedMethodId)
        .single();

      throwIfConversationLoadError(methodError, 'thinking_methods', 'load selected method');

      // Fetch messages — only when we have a real, persisted conversation.
      let messagesArray: Message[] = [];
      let contextDocsArray: ContextDocument[] = [];
      if (!(conversationData as Conversation).pending && conversationData.id) {
        const { data: messagesData, error: messagesError } = await supabase
          .from('messages_vr')
          .select('*')
          .eq('conversation_id', conversationData.id)
          .order('created_at', { ascending: true });

        throwIfConversationLoadError(messagesError, 'messages_vr', 'load messages');
        messagesArray = (messagesData || []) as Message[];

        const { data: contextDocsData, error: contextDocsError } = await supabase
          .from('conversation_context_documents')
          .select('id, storage_path, file_name, content_sha256, content_type, file_size, active, status, error, created_at, updated_at')
          .eq('conversation_id', conversationData.id)
          .order('created_at', { ascending: true });
        throwIfConversationLoadError(contextDocsError, 'conversation_context_documents', 'load documents');
        contextDocsArray = (contextDocsData || []) as ContextDocument[];
      }

      if (
        boardMode &&
        userId &&
        messagesArray.length === 0 &&
        !(conversationData as Conversation).pending &&
        conversationData.id &&
        conversationMethodOrder.length >= 2
      ) {
        messagesArray = await buildInitialBoardGreetingMessages({
          conversationId: conversationData.id,
          userId,
          methodIds: conversationMethodOrder,
          lenses: [methodData, ...visibleLenses],
        });
      }

      const messageLensIds = Array.from(new Set(
        messagesArray
          .map((message) => message.thinking_method_id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      ));
      const knownLensIds = new Set([resolvedMethodId, ...conversationMethodOrder, ...visibleLenses.map((lens) => lens.id)]);
      const missingLensIds = messageLensIds.filter((id) => !knownLensIds.has(id));
      if (missingLensIds.length > 0) {
        const { data: historicalLenses, error: historicalLensesError } = await supabase
          .from('thinking_methods')
          .select('id, name')
          .in('id', missingLensIds);
        throwIfConversationLoadError(historicalLensesError, 'thinking_methods', 'load historical methods');
        if (historicalLenses?.length) {
          setLensNameById((prev) => ({
            ...prev,
            ...Object.fromEntries(historicalLenses.map((lens) => [lens.id, lens.name])),
          }));
        }
      }
      setLensNameById((prev) => ({
        ...prev,
        [methodData.id]: methodData.name,
      }));
      setThinkingMethod(methodData);
      setSelectedLensIds(conversationMethodOrder.length > 0 ? conversationMethodOrder : [resolvedMethodId]);
      setDocumentLensId((prev) => {
        const order = conversationMethodOrder.length > 0 ? conversationMethodOrder : [resolvedMethodId!];
        return prev && order.includes(prev) ? prev : order[0];
      });
      setConversation({
        ...conversationData,
        thinking_method_id: resolvedMethodId,
      });
      autoScrollEnabledRef.current = true;
      lastMessageIdRef.current = null;

      // If brand-new (pending or empty) conversation, show the greeting locally
      // without persisting it. It will be persisted alongside the conversation
      // the moment the user sends their first message.
      if (messagesArray.length === 0 && resolvedMethodId) {
        const { data: greetingAttr, error: greetingAttrError } = await supabase
          .from('thinking_method_attributes')
          .select('attribute_value')
          .eq('thinking_method_id', resolvedMethodId)
          .eq('attribute_name', 'GREETING')
          .maybeSingle();
        if (greetingAttrError?.code !== 'PGRST116') {
          throwIfConversationLoadError(greetingAttrError, 'thinking_method_attributes', 'load greeting');
        }

        if (greetingAttr?.attribute_value) {
          const localGreeting: Message = {
            id: `${LOCAL_GREETING_ID_PREFIX}${resolvedMethodId}`,
            content: greetingAttr.attribute_value,
            is_user: false,
            created_at: new Date().toISOString(),
            kind: 'CHAT',
            thinking_method_id: resolvedMethodId,
          };
          messagesArray = [localGreeting];
        }
      }

      setMessages(messagesArray);
      const recentDocuments = Array.from(recentlyUploadedContextDocumentsRef.current.values())
        .filter(({ conversationId }) => conversationId === conversationData.id)
        .map(({ document }) => document);
      const recentById = new Map(recentDocuments.map(document => [document.id, document]));
      updateContextDocuments(prev => {
        const merged = mergeContextDocumentLists(
          mergeContextDocumentLists(contextDocsArray, recentDocuments),
          prev.filter(document => document.status === 'uploading'),
        );
        return merged.map(document => {
          const replacing = prev.find(item => item.id === document.id && item.replacementUploading);
          return replacing ?? recentById.get(document.id) ?? document;
        });
      });
      pruneCancelledMessages(messagesArray);

      // Find the last assistant message
      const lastAssistantMessage = [...messagesArray]
        .reverse()
        .find(m => !m.is_user);
      
      if (lastAssistantMessage && lastAssistantMessage.kind === "QUESTION") {
        // Check if there's an ANSWER after this QUESTION
        const hasAnswerAfter = messagesArray.some(
          m => m.kind === "ANSWER" && 
               m.parent_message_id === lastAssistantMessage.id &&
               new Date(m.created_at) > new Date(lastAssistantMessage.created_at)
        );
        
        if (!hasAnswerAfter) {
          setInvestigationInProgress(true);
          setInvestigationQuestionId(lastAssistantMessage.id);
        } else {
          setInvestigationInProgress(false);
          setInvestigationQuestionId(null);
        }
      } else {
        setInvestigationInProgress(false);
        setInvestigationQuestionId(null);
      }
    } catch (error) {
      if (error instanceof ConversationNotFoundError) {
        console.info('Conversation not found; redirecting home.');
        toast({
          title: "Conversation unavailable",
          description: "It may have been deleted or opened from another account.",
        });
        setLoading(false);
        navigate('/', { replace: true });
        return;
      }

      playErrorSound();
      handleConversationLoadFailure({
        error,
        development: import.meta.env.DEV,
        clearLoading: () => setLoading(false),
        log: (message, details) => console.error(message, details),
        toast,
        navigate: (path) => navigate(path),
      });
      return;
    }
    setLoading(false);
  };

  const persistPendingConversation = async ({
    currentConversation,
    activeMethodOrder,
    primaryConversationLensId,
    targetSurface = boardMode ? 'board' : 'chat',
    boardName,
  }: {
    currentConversation: Conversation;
    activeMethodOrder: string[];
    primaryConversationLensId: string;
    targetSurface?: 'chat' | 'board';
    boardName?: string | null;
  }): Promise<{ conversation: Conversation; persistedGreetingId: string | null }> => {
    if (!currentConversation.pending && currentConversation.id) {
      return { conversation: currentConversation, persistedGreetingId: null };
    }

    const { data: newConversationData, error: createError } = await supabase
      .from('conversations')
      .insert({
        user_id: user?.id,
        thinking_method_id: primaryConversationLensId,
        openai_conversation_id: buildOptionalConversationMethodsPayload(activeMethodOrder, { name: boardName }),
      })
      .select()
      .single();
    if (createError || !newConversationData) {
      throw createError ?? new Error('Failed to create conversation');
    }

    const persistedConversation: Conversation = {
      id: newConversationData.id,
      thinking_method_id: newConversationData.thinking_method_id ?? undefined,
      openai_conversation_id: newConversationData.openai_conversation_id ?? null,
    };

    const localGreeting = activePath.find(m => isLocalGreetingId(m.id));
    let persistedGreetingId: string | null = null;
    if (localGreeting) {
      const { data: greetingMsg } = await supabase
        .from('messages_vr')
        .insert({
          conversation_id: persistedConversation.id,
          is_user: false,
          kind: 'CHAT',
          content: localGreeting.content,
          thinking_method_id: localGreeting.thinking_method_id ?? persistedConversation.thinking_method_id ?? thinkingMethod?.id,
          properties: { avoid_context: true },
        })
        .select()
        .single();
      if (greetingMsg) {
        persistedGreetingId = greetingMsg.id;
        setMessages(prev =>
          prev.map(m =>
            isLocalGreetingId(m.id) ? (greetingMsg as Message) : m
          )
        );
      }
    }

    setConversation(persistedConversation);
    conversationRef.current = persistedConversation;
    suppressNextRouteFetchRef.current = persistedConversation.id;
    navigate(`${targetSurface === 'board' ? '/board' : '/chat'}/${persistedConversation.id}${applicationsOriginSearch}`, { replace: true });

    return { conversation: persistedConversation, persistedGreetingId };
  };

  const activeContextDocumentIds = useMemo(
    () =>
      contextDocuments
        .filter((document) => document.active && document.status === 'ready')
        .map((document) => document.id),
    [contextDocuments],
  );

  const formatContextDocumentSize = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const ensureContextDocumentConversation = async (): Promise<Conversation> => {
    const currentConversation = conversationRef.current;
    if (!currentConversation || !thinkingMethod) {
      throw new Error('Conversation is not ready');
    }

    const methodOrder = uniqueConversationMethodIds(
      selectedLensIds.length > 0
        ? selectedLensIds
        : [currentConversation.thinking_method_id ?? thinkingMethod.id],
    );
    const primaryLensId = methodOrder[0] ?? currentConversation.thinking_method_id ?? thinkingMethod.id;
    const persisted = await persistPendingConversation({
      currentConversation,
      activeMethodOrder: methodOrder,
      primaryConversationLensId: primaryLensId,
    });
    return persisted.conversation;
  };

  const handleContextDocumentUploadClick = () => {
    if (contextDocumentUploading || sending || deepThinking || switchingLens) return;
    contextDocumentInputRef.current?.click();
  };

  const uploadContextDocument = async (
    file: File,
    contentSha256: string,
    documentId: string,
    replacement?: ContextDocument,
  ) => {
    if (!getSupportedContextDocumentKind(file)) {
      throw new Error('Unsupported source file type.');
    }
    if (!userId) throw new Error('Session required. Please refresh and try again.');
    let uploadedPath: string | null = null;

    try {
      const serverNormalized = isServerNormalizedContextDocument(file);
      const localText = serverNormalized ? null : await extractContextDocumentText(file);
      const persistedConversation = await ensureContextDocumentConversation();
      const safeFileName = sanitizeContextDocumentFileName(file.name);
      const storagePath = `${userId}/${persistedConversation.id}/${documentId}/${createUuid()}-${safeFileName}`;
      const { error: intentError } = await supabase.rpc('begin_context_document_upload', { p_conversation_id: persistedConversation.id, p_path: storagePath });
      if (intentError) throw intentError;
      uploadedPath = storagePath;

      const { error: uploadError } = await supabase.storage
        .from(CONTEXT_DOCUMENT_BUCKET)
        .upload(storagePath, file, {
          contentType: file.type || undefined,
          upsert: false,
        });
      if (uploadError) throw uploadError;

      let extractedText = localText;
      if (serverNormalized) {
        const { data, error } = await supabase.functions.invoke('normalize-conversation-document', {
          body: { conversationId: persistedConversation.id, storagePath },
        });
        if (error || typeof data?.text !== 'string' || !data.text.trim()) {
          let message = 'Could not extract this source file.';
          const response = error && 'context' in error ? error.context : null;
          if (response instanceof Response) {
            const detail = await response.json().catch(() => null);
            if (typeof detail?.error === 'string') message = detail.error;
          }
          throw new Error(message);
        }
        extractedText = data.text;
      }

      const sourceFields = {
        storage_path: storagePath,
        file_name: file.name,
        content_sha256: contentSha256,
        content_type: file.type || null,
        file_size: file.size,
        extracted_text: extractedText ?? '',
        status: 'ready',
        error: null,
      };
      const columns = 'id, storage_path, file_name, content_sha256, content_type, file_size, active, status, error, created_at, updated_at';
      const result = replacement
        ? await supabase.from('conversation_context_documents')
          .update(sourceFields)
          .eq('id', documentId)
          .eq('conversation_id', persistedConversation.id)
          .select(columns)
          .single()
        : await supabase.from('conversation_context_documents')
          .insert({
            ...sourceFields,
            id: documentId,
            conversation_id: persistedConversation.id,
            user_id: userId,
            active: true,
          })
          .select(columns)
          .single();
      if (result.error || !result.data) throw result.error ?? new Error('Failed to save source document');
      const savedDoc = result.data as ContextDocument;

      uploadedPath = null;
      recentlyUploadedContextDocumentsRef.current.set(savedDoc.id, {
        conversationId: persistedConversation.id,
        document: savedDoc,
      });
      updateContextDocuments(prev => replaceContextDocument(prev, savedDoc));
      // Replacement cleanup is queued transactionally by the database.
      toast({
        title: replacement ? 'Source replaced' : 'Source added',
        description: `${file.name} will be used as conversation context.`,
      });
    } catch (error) {
      if (uploadedPath) {
        const { error: cleanupError } = await supabase.storage.from(CONTEXT_DOCUMENT_BUCKET).remove([uploadedPath]);
        if (cleanupError) console.warn('Source document cleanup failed:', cleanupError);
      }
      throw error;
    }
  };

  const handleContextDocumentFiles = async (files: readonly File[]) => {
    if (files.length === 0 || isInputDisabled) return;
    contextDocumentUploadBatchesRef.current += 1;
    setContextDocumentUploading(true);
    const work = contextDocumentQueueRef.current.then(async () => {
      let quotaRejected = 0;
      try {
        await processContextDocumentFiles(files, async file => {
          if (!getSupportedContextDocumentKind(file)) {
            throw new Error('Unsupported source file type.');
          }
          if (file.size > MAX_CONTEXT_DOCUMENT_FILE_BYTES) {
            throw new Error('File is too large. Upload a file up to 10 MB.');
          }
          const contentSha256 = await sha256ContextDocumentBytes(file);
          const existing = contextDocumentsRef.current.find(document =>
            normalizeContextDocumentName(document.file_name) === normalizeContextDocumentName(file.name)
          );
          let documentsForDecision = contextDocumentsRef.current;
          if (existing && !existing.content_sha256) {
            if (!existing.storage_path) throw new Error('Could not compare the existing source file. Try again.');
            const { data, error } = await supabase.storage
              .from(CONTEXT_DOCUMENT_BUCKET).download(existing.storage_path);
            if (error || !data) throw new Error('Could not compare the existing source file. Try again.');
            const storedSha256 = await sha256ContextDocumentBytes(data);
            documentsForDecision = contextDocumentsRef.current.map(document =>
              document.id === existing.id ? { ...document, content_sha256: storedSha256 } : document
            );
          }
          const decision = await resolveContextDocumentUpload(
            documentsForDecision,
            file.name,
            contentSha256,
            source => window.confirm(`Replace the existing source file "${source.file_name}" with "${file.name}"?`),
          );
          if (decision.kind === 'duplicate' || decision.kind === 'cancelled') return;
          if (decision.kind === 'quota') {
            quotaRejected += 1;
            return;
          }
          setSourceDocumentsOpen(true);
          if (decision.kind === 'replace') {
            const previous = contextDocumentsRef.current.find(document => document.id === decision.existing.id);
            if (!previous) return;
            updateContextDocuments(current => current.map(document =>
              document.id === previous.id ? { ...document, replacementUploading: true } : document
            ));
            try {
              await uploadContextDocument(file, contentSha256, previous.id, previous);
            } catch (error) {
              updateContextDocuments(current => current.map(document =>
                document.id === previous.id ? previous : document
              ));
              throw error;
            }
            return;
          }
          const id = createUuid();
          updateContextDocuments(current => [...current, {
            id,
            file_name: file.name,
            file_size: file.size,
            content_sha256: contentSha256,
            active: false,
            status: 'uploading',
            created_at: new Date().toISOString(),
          }]);
          try {
            await uploadContextDocument(file, contentSha256, id);
          } catch (error) {
            updateContextDocuments(current => current.filter(document => document.id !== id));
            throw error;
          }
        }, (file, error) => {
          console.error(`Context document upload failed for ${file.name}:`, error);
          playErrorSound();
          toast({
            title: `Could not upload ${file.name}`,
            description: getContextDocumentUploadErrorMessage(error),
            variant: 'destructive',
          });
        });
        if (quotaRejected > 0) {
          playErrorSound();
          toast({
            title: `${quotaRejected} source ${quotaRejected === 1 ? 'file' : 'files'} not added`,
            description: CONTEXT_DOCUMENT_QUOTA_MESSAGE,
            variant: 'destructive',
          });
        }
      } finally {
        contextDocumentUploadBatchesRef.current -= 1;
        if (contextDocumentUploadBatchesRef.current === 0) setContextDocumentUploading(false);
      }
    });
    contextDocumentQueueRef.current = work.catch(error => {
      console.error('Source document batch failed:', error);
      playErrorSound();
      toast({ title: 'Could not process source files', description: getContextDocumentUploadErrorMessage(error), variant: 'destructive' });
    });
    await contextDocumentQueueRef.current;
  };

  const handleContextDocumentSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void handleContextDocumentFiles(files);
  };

  const handleContextDocumentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    void handleContextDocumentFiles(Array.from(event.dataTransfer.files));
  };

  const handleContextDocumentDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = isInputDisabled ? 'none' : 'copy';
  };

  const toggleContextDocumentActive = async (document: ContextDocument, active: boolean) => {
    if (active && !document.active && countReservedContextDocumentSlots(contextDocumentsRef.current) >= MAX_ACTIVE_CONTEXT_DOCUMENTS) {
      playErrorSound();
      toast({ title: 'Source limit reached', description: CONTEXT_DOCUMENT_QUOTA_MESSAGE, variant: 'destructive' });
      return;
    }
    const recentUpload = recentlyUploadedContextDocumentsRef.current.get(document.id);
    const updateActive = (nextActive: boolean) => {
      if (recentUpload) recentlyUploadedContextDocumentsRef.current.set(document.id, {
        ...recentUpload,
        document: { ...recentUpload.document, active: nextActive },
      });
      updateContextDocuments(prev =>
        prev.map(item => item.id === document.id ? { ...item, active: nextActive } : item)
      );
    };
    // Keep a slot reserved until a deactivation is confirmed by the server.
    if (active) updateActive(true);
    const { error } = await supabase
      .from('conversation_context_documents')
      .update({ active })
      .eq('id', document.id);
    if (error) {
      if (active) updateActive(document.active);
      playErrorSound();
      toast({
        title: 'Update failed',
        description: 'Could not update the source document.',
        variant: 'destructive',
      });
    } else if (!active) {
      updateActive(false);
    }
  };

  const deleteContextDocument = async (document: ContextDocument) => {
    const { error } = await supabase
      .from('conversation_context_documents')
      .delete()
      .eq('id', document.id);
    if (error) {
      playErrorSound();
      toast({
        title: 'Delete failed',
        description: 'Could not delete the source document.',
        variant: 'destructive',
      });
      return;
    }
    recentlyUploadedContextDocumentsRef.current.delete(document.id);
    updateContextDocuments(prev => prev.filter(item => item.id !== document.id));

    if (document.storage_path) {
      const { error: storageError } = await supabase.storage
        .from(CONTEXT_DOCUMENT_BUCKET)
        .remove([document.storage_path]);
      if (storageError) {
        console.warn('Source document file cleanup failed:', storageError);
      }
    }
  };

  const sendMessage = async (useDeepMode = false) => {
    // Use ref to get the latest conversation to avoid stale closures after delete/recreate
    let currentConversation = conversationRef.current;
    
    if (!newMessage.trim() || !currentConversation || !thinkingMethod || sending || deepThinking || switchingLens) return;

    // Cancel any in-progress message edit
    if (editingMessageId) cancelEdit();

    if (hasPendingResponse) {
      toast({
        title: "Please wait",
        description: "Your previous request is still in progress.",
      });
      return;
    }

    console.log('sendMessage:start', {
      textLen: newMessage.length,
      useDeepMode,
      conversationId: currentConversation?.id,
      thinkingMethod: thinkingMethod?.name,
    });

    const activeMethodOrder = uniqueConversationMethodIds(
      selectedLensIds.length > 0
        ? selectedLensIds
        : [currentConversation.thinking_method_id ?? thinkingMethod.id]
    );
    const primaryConversationLensId = activeMethodOrder[0] ?? currentConversation.thinking_method_id ?? thinkingMethod.id;
    const pickerDocumentLensId =
      documentLensId && activeMethodOrder.includes(documentLensId)
        ? documentLensId
        : primaryConversationLensId;

    setStreamingResponse(null);

    // Determine message kind based on context and switch state
    // If investigation is in progress, force ANSWER kind regardless of other checks
    // If editing an artifact, force EDIT kind
    // If investigation is in progress but user turned off document mode, send as CHAT
    const lastMessage = activePath.length > 0 ? activePath[activePath.length - 1] : null;
    const messageKind = investigationInProgress && investigationQuestionId && useDeepMode
      ? "ANSWER" 
      : determineMessageKind(useDeepMode, lastMessage, editingArtifactId);
    const isDeepThinking = messageKind === "ARTF" || messageKind === "ANSWER" || messageKind === "EDIT";

    if (isDeepThinking && documentLimitReached) {
      playErrorSound();
      promptDocumentUpgrade();
      return;
    }

    // Pre-flight credit check for expensive Document-generating kinds (registered users only)
    if (
      isDeepThinking &&
      !isAnonymous &&
      !creditsLoading &&
      creditsBalance < DOCUMENT_CREDIT_THRESHOLD
    ) {
      playErrorSound();
      trackEvent('credits_exhausted', {
        lens_id: pickerDocumentLensId,
        mode: 'document',
        source: 'send_message_preflight',
        status: 'insufficient_credits',
      });
      toast({
        title: 'Not enough credits to create an App’s Document',
        description: `This step needs at least ${DOCUMENT_CREDIT_THRESHOLD} credits (you have ${creditsBalance}). Request more credits to continue.`,
        variant: 'destructive',
        action: (
          <ToastAction altText="Request more credits" onClick={() => trackCreditsRequestNavigation('send_message_preflight_toast')}>
            Request more credits
          </ToastAction>
        ),
      });
      return;
    }

    // Clear editing state after determining kind (we'll use it in the request)
    const currentEditingArtifactId = editingArtifactId;
    const findMessageById = (id: string | null | undefined) =>
      id
        ? messages.find(m => m.id === id) ?? activePath.find(m => m.id === id) ?? null
        : null;
    const sourceQuestionLensId =
      messageKind === "ANSWER"
        ? getAssistantLensId(findMessageById(investigationQuestionId))
        : null;
    const sourceArtifactLensId =
      messageKind === "EDIT"
        ? getAssistantLensId(findMessageById(currentEditingArtifactId))
        : null;
    const activeDocumentLensId = sourceQuestionLensId ?? sourceArtifactLensId ?? pickerDocumentLensId;
    const requestThinkingMethodId = isDeepThinking ? activeDocumentLensId : primaryConversationLensId;
    setEditingArtifactId(null);

    trackEvent('prompt_started', {
      lens_id: requestThinkingMethodId,
      lens_type: boardMode ? 'board' : 'single',
      message_kind: messageKind,
      mode: isDeepThinking ? 'document' : 'chat',
      page_path: location.pathname,
    });

    // Keep the existing document reachable from the mobile switcher while a reply is sent.
    if (window.matchMedia('(min-width: 768px)').matches) setSelectedArtifactId(null);
    autoScrollEnabledRef.current = true;

    if (isDeepThinking) {
      setSending(false);
      setDeepThinking(true);
    } else {
      setSending(true);
    }

    const messageContent = newMessage.trim();
    setNewMessage('');

    // Generate client message ID for idempotency and cancellation tracking
    const clientMessageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    activeClientMessageIdRef.current = clientMessageId;

    // Show immediate streaming placeholder tied to temp id
    const tempParentId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? (crypto).randomUUID()
      : '00000000-0000-4000-8000-' + Math.random().toString(16).slice(2, 14).padEnd(12, '0');

    try {
      // Refresh token before making edge function call if using deep thinking
      if (isDeepThinking) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          playErrorSound();
          toast({
            title: "Session Expired",
            description: "Please log out and log back in to continue",
            variant: "destructive",
          });
          return;
        }
      }

      // Lazy conversation persistence: if this is the first user message in a
      // pending (in-memory) conversation, create the DB row now AND persist any
      // local greeting message that's currently sitting in `messages` state.
      // This is what stops "Empty conversation" rows from appearing.
      let persistedGreetingId: string | null = null;
      if (currentConversation.pending || !currentConversation.id) {
        const persisted = await persistPendingConversation({
          currentConversation,
          activeMethodOrder,
          primaryConversationLensId,
        });
        currentConversation = persisted.conversation;
        persistedGreetingId = persisted.persistedGreetingId;
      }

      // For ANSWER messages, the parent should be the QUESTION message.
      // For other messages, the parent should be the last message in the active path.
      // If the last visible message is the local greeting we just persisted above,
      // use the new persisted id (since `activePath` won't have re-derived yet).
      const lastVisibleMessageRaw = activePath.length > 0 ? activePath[activePath.length - 1] : null;
      const lastVisibleMessageId =
        lastVisibleMessageRaw && isLocalGreetingId(lastVisibleMessageRaw.id)
          ? persistedGreetingId
          : lastVisibleMessageRaw?.id ?? null;
      const userMessageParentId = messageKind === "ANSWER" && investigationQuestionId
        ? investigationQuestionId
        : lastVisibleMessageId;

      // Add user message to UI immediately with a temporary UUID id to avoid DB uuid errors
      const tempId = tempParentId;

      const userMessage: Message = {
        id: tempId,
        content: messageContent,
        is_user: true,
        created_at: new Date().toISOString(),
        kind: messageKind,
        thinking_method_id: requestThinkingMethodId,
        client_message_id: clientMessageId,
        parent_message_id: userMessageParentId ?? undefined,  // Include parent for proper display
      };
      setMessages(prev => [...prev, userMessage]);

      // Reset investigation state immediately when user sends their answer
      // This clears the "Investigation in progress" indicator right when the message is sent
      if (messageKind === "ANSWER") {
        setInvestigationInProgress(false);
        setInvestigationQuestionId(null);
      }

      if (isClientMessageCancelled(clientMessageId)) {
        return;
      }

      // Show immediate streaming placeholder before the network responds
      setStreamingResponse({
        parentMessageId: tempId,
        output: '',
        reasoning: 'Thinking...',
        isDeepThinking,
      });
      
      console.log('stream:start', {
        parentMessageId: tempId,
        isDeepThinking,
        clientMessageId,
      });

      // Call OpenAI API via edge function and stream reasoning/output
      // User message will be inserted by the edge function
      const controller = new AbortController();
      streamAbortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000); // 10 minutes
      const streamUrl = `${SUPABASE_URL}/functions/v1/chat-with-thinking-method`;
      const session = (await supabase.auth.getSession()).data.session;
      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error('Unable to start your guest session. Refresh and try again.');
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': SUPABASE_PUBLISHABLE_KEY,
        'Accept': 'text/event-stream',
      };

      // Simplified payload - user message insertion is handled by the backend
      const requestBody: Record<string, unknown> = {
        message: messageContent,
        thinkingMethodId: requestThinkingMethodId,
        dbConversationId: currentConversation.id,
        kind: messageKind,
        clientMessageId,
        userMessageParentId,  // Parent for the user message (not the user message itself)
        stream: true,
        forceJson: false, // Use true SSE streaming to prevent gateway timeout on long requests
      };
      if (activeContextDocumentIds.length > 0) {
        requestBody.contextDocumentIds = activeContextDocumentIds;
      }
      
      // Include editing artifact ID if editing
      if (messageKind === "EDIT" && currentEditingArtifactId) {
        requestBody.editingArtifactId = currentEditingArtifactId;
      }

      trackEvent('prompt_submitted', {
        lens_id: requestThinkingMethodId,
        lens_type: boardMode ? 'board' : 'single',
        message_kind: messageKind,
        mode: isDeepThinking ? 'document' : 'chat',
        page_path: location.pathname,
      });

      let streamError = null;
      let finalStreamData = null;

      try {
        // Retry fetch once on transient network errors (e.g. "Failed to fetch")
        let response: Response;
        const MAX_FETCH_RETRIES = 2;
        for (let attempt = 1; attempt <= MAX_FETCH_RETRIES; attempt++) {
          try {
            response = await fetch(streamUrl, {
              method: 'POST',
              headers,
              body: JSON.stringify(requestBody),
              signal: controller.signal,
            });
            break; // success
          } catch (fetchErr: unknown) {
            const isNetworkError =
              (fetchErr instanceof Error ? fetchErr.name : "") === 'TypeError' ||
              String((fetchErr instanceof Error ? fetchErr.message : "Network request failed")).includes('Failed to fetch') ||
              String((fetchErr instanceof Error ? fetchErr.message : "Network request failed")).includes('NetworkError') ||
              String((fetchErr instanceof Error ? fetchErr.message : "Network request failed")).includes('network');
            if (isNetworkError && attempt < MAX_FETCH_RETRIES) {
              console.warn(`[sendMessage] Network error on attempt ${attempt}, retrying in 1.5s...`, fetchErr);
              await new Promise((r) => setTimeout(r, 1500));
              continue;
            }
            throw fetchErr;
          }
        }

        if (!response.ok) {
          const errorText = await response.text();

            // Handle paywall (insufficient credits / guest limit)
          if (response.status === 402) {
            let parsed = null;
            try { parsed = JSON.parse(errorText); } catch { /* Optional cleanup must not interrupt the primary action. */ }
            const reason = parsed?.error;
            trackEvent('credits_exhausted', {
              lens_id: requestThinkingMethodId,
              mode: isDeepThinking ? 'document' : 'chat',
              source: 'edge_function',
              status: typeof reason === 'string' ? reason : 'insufficient_credits',
            });
            setStreamingResponse(null);

            if (reason === 'document_limit_reached') {
              markMessageCancelled(clientMessageId);
              setUseDeepThinkingMode(false);
              setEditingArtifactId(null);
              refreshCredits();
              toast({
                title: 'Free preview document limit reached',
                description: parsed?.message || 'Create an account to keep creating documents.',
                variant: 'destructive',
              });
              return;
            }

            if (reason === 'guest_document_limit' || reason === 'guest_ip_limit_reached') {
              // Mark this client message as cancelled so the optimistic user
              // message doesn't keep the UI stuck on "Thinking..." (it has no
              // assistant child because the backend rejected before generating).
              markMessageCancelled(clientMessageId);
              const limitReason = reason === 'guest_ip_limit_reached'
                ? "You've reached the free preview limit for this device. Apply for Applyt beta access to keep exploring — we'll send you an invitation if a seat is available."
                : undefined;
              openConvertPrompt(limitReason, reason === 'guest_ip_limit_reached' ? "You've reached the free preview limit" : undefined);
              return;
            }

            const description = parsed?.message || 'You have run out of credits.';
            markMessageCancelled(clientMessageId);
            toast({
              title: 'Out of credits',
              description,
              variant: 'destructive',
            });
            // Redirect to upgrade page after a short delay
            setTimeout(() => navigate('/pricing'), 1200);
            return;
          }

          // Handle stale conversation ID - conversation was deleted but sendMessage used old ID
          if (response.status === 404 && errorText.includes('conversation_not_found')) {
            console.warn('Conversation not found - refreshing data and retrying');
            toast({
              title: "Refreshing conversation",
              description: "Please try sending your message again.",
            });
            setStreamingResponse(null);
            await fetchData();
            return;
          }
          
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        // Parse real SSE stream for live updates
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let accumulatedReasoning = '';
        let accumulatedOutput = '';
        let sseBuffer = '';
        let jsonPayload = null;
        let streamFinished = false;
        let lastEventAt = Date.now();
        const idleWatcher = setInterval(() => {
          // Abort if no SSE activity for 90 seconds to avoid hanging UI
          // Edge functions have a 150s timeout, so if nothing arrives for 90s
          // the function has likely crashed without closing the connection
          if (Date.now() - lastEventAt > 90_000) {
            console.warn('[stream] Idle timeout reached – aborting stale connection');
            controller.abort();
          }
        }, 10_000);
        const updateConferenceStreamResult = (parsed: Record<string, unknown>, status: ConferenceStreamResult['status']) => {
          const numericIndex = Number(parsed?.index);
          const index = Number.isFinite(numericIndex) ? numericIndex : 0;
          const numericCount = Number(parsed?.count);
          const count = Number.isFinite(numericCount) && numericCount > 0 ? numericCount : selectedLensIds.length || 1;
          const content = typeof parsed?.content === 'string' ? parsed.content : '';

          setStreamingResponse((prev) => {
            if (!prev) return prev;
            const existingResults = prev.conferenceResults ?? [];
            const existing = existingResults.find((result) => result.index === index);
            const nextResult: ConferenceStreamResult = {
              ...existing,
              index,
              count,
              thinkingMethodId: typeof parsed?.thinkingMethodId === 'string'
                ? parsed.thinkingMethodId
                : existing?.thinkingMethodId,
              lensName: typeof parsed?.lensName === 'string'
                ? parsed.lensName
                : existing?.lensName,
              lensAuthor: typeof parsed?.lensAuthor === 'string'
                ? parsed.lensAuthor
                : existing?.lensAuthor,
              content: content || existing?.content || '',
              status,
              messageId: typeof parsed?.messageId === 'string'
                ? parsed.messageId
                : existing?.messageId,
              kind: typeof parsed?.kind === 'string'
                ? parsed.kind
                : existing?.kind,
              error: typeof parsed?.error === 'string'
                ? parsed.error
                : existing?.error,
            };
            const nextResults = [
              ...existingResults.filter((result) => result.index !== index),
              nextResult,
            ].sort((a, b) => a.index - b.index);

            return { ...prev, conferenceResults: nextResults };
          });
        };
        const hydrateConferenceStreamResultContent = async (
          parsed: Record<string, unknown>,
          status: ConferenceStreamResult['status'],
        ) => {
          const messageId = typeof parsed?.messageId === 'string' ? parsed.messageId : '';
          const inlineContent = typeof parsed?.content === 'string' ? parsed.content.trim() : '';
          if (!messageId || inlineContent) return;

          try {
            const { data, error } = await supabase
              .from('messages_vr')
              .select('content, kind, thinking_method_id')
              .eq('id', messageId)
              .maybeSingle();

            if (error || !data || typeof data.content !== 'string' || data.content.trim().length === 0) {
              return;
            }

            updateConferenceStreamResult({
              ...parsed,
              content: data.content,
              kind: typeof data.kind === 'string' ? data.kind : parsed.kind,
              thinkingMethodId: typeof data.thinking_method_id === 'string'
                ? data.thinking_method_id
                : parsed.thinkingMethodId,
            }, status);
          } catch (hydrateError) {
            console.warn('Failed to hydrate streamed Expert answer:', hydrateError);
          }
        };

        if (reader) {
          try {
            while (!streamFinished) {
              const { done, value } = await reader.read();
              if (done) break;
              lastEventAt = Date.now();
              
              const chunk = decoder.decode(value, { stream: true });
              sseBuffer += chunk;
              
              // Process complete SSE events (handle LF and CRLF separators)
              const findBoundary = () => {
                const lfIndex = sseBuffer.indexOf('\n\n');
                const crlfIndex = sseBuffer.indexOf('\r\n\r\n');
                if (crlfIndex !== -1 && (lfIndex === -1 || crlfIndex < lfIndex)) {
                  return { boundary: crlfIndex, len: 4 };
                }
                if (lfIndex !== -1) {
                  return { boundary: lfIndex, len: 2 };
                }
                return null;
              };

              while (true) {
                const boundaryInfo = findBoundary();
                if (!boundaryInfo) break;
                const eventBlock = sseBuffer.slice(0, boundaryInfo.boundary);
                sseBuffer = sseBuffer.slice(boundaryInfo.boundary + boundaryInfo.len);
                
                // Parse SSE event block (may contain event: and data: lines)
                let eventType = 'message';
                let dataStr = '';
                
                for (const line of eventBlock.split('\n')) {
                  if (line.startsWith('event:')) {
                    eventType = line.slice(6).trim();
                  } else if (line.startsWith('data:')) {
                    dataStr += line.slice(5).trim();
                  }
                }
                
                if (!dataStr || dataStr === '[DONE]') continue;
                
                try {
                  const parsed = JSON.parse(dataStr);
                  
                  // Handle different event types based on event: header
                  if (eventType === 'reasoning') {
                    // Reasoning event: use delta or full
                    if (parsed.delta) {
                      accumulatedReasoning += parsed.delta;
                    } else if (parsed.full) {
                      accumulatedReasoning = parsed.full;
                    }
                    setStreamingResponse(prev => prev ? { ...prev, reasoning: accumulatedReasoning } : prev);
                  } else if (eventType === 'output') {
                    // Output event: use delta or full
                    if (parsed.delta) {
                      accumulatedOutput += parsed.delta;
                    } else if (parsed.full) {
                      accumulatedOutput = parsed.full;
                    }
                    // Update both output AND reasoning (for non-o1 models that don't stream reasoning separately)
                    setStreamingResponse(prev => prev ? { 
                      ...prev, 
                      output: accumulatedOutput,
                      // Show output in Progress section if no dedicated reasoning stream
                      reasoning: accumulatedReasoning || accumulatedOutput
                    } : prev);
                  } else if (eventType === 'done') {
                    // Final payload with metadata
                    jsonPayload = parsed;
                    streamFinished = true;
                  } else if (eventType === 'stream-start') {
                    // Stream started, update UI - show initial progress
                    // Stream started - no need to show placeholder text
                  } else if (eventType === 'conference-start') {
                    accumulatedReasoning = boardMode
                      ? `Board started with ${parsed.count || 0} Experts.`
                      : `Conference started with ${parsed.count || 0} lenses.`;
                    setStreamingResponse(prev => prev ? { ...prev, reasoning: accumulatedReasoning, conferenceResults: [] } : prev);
                  } else if (eventType === 'conference-lens-start') {
                    const lensName = parsed.lensAuthor || parsed.lensName || parsed.thinkingMethodId || (boardMode ? 'Expert' : 'Lens');
                    accumulatedReasoning += `${accumulatedReasoning ? '\n' : ''}${lensName} is thinking...`;
                    updateConferenceStreamResult(parsed, 'thinking');
                    setStreamingResponse(prev => prev ? { ...prev, reasoning: accumulatedReasoning } : prev);
                  } else if (eventType === 'conference-lens-done') {
                    const lensName = parsed.lensAuthor || parsed.lensName || parsed.thinkingMethodId || (boardMode ? 'Expert' : 'Lens');
                    accumulatedReasoning += ` done.`;
                    updateConferenceStreamResult(parsed, 'done');
                    void hydrateConferenceStreamResultContent(parsed, 'done');
                    setStreamingResponse(prev => prev ? { ...prev, reasoning: accumulatedReasoning || `${lensName} finished.` } : prev);
                  } else if (eventType === 'conference-lens-error') {
                    const lensName = parsed.lensAuthor || parsed.lensName || parsed.thinkingMethodId || (boardMode ? 'Expert' : 'Lens');
                    accumulatedReasoning += `${accumulatedReasoning ? '\n' : ''}${lensName} failed; continuing.`;
                    updateConferenceStreamResult(parsed, 'error');
                    void hydrateConferenceStreamResultContent(parsed, 'error');
                    setStreamingResponse(prev => prev ? { ...prev, reasoning: accumulatedReasoning } : prev);
                  } else {
                    // Unknown event type, check parsed content for hints
                    if (parsed.response || parsed.text) {
                      jsonPayload = parsed;
                    } else if (parsed.delta) {
                      accumulatedOutput += parsed.delta;
                      setStreamingResponse(prev => prev ? { ...prev, output: accumulatedOutput } : prev);
                    }
                  }
                } catch (e) {
                  // Non-JSON SSE data, skip
                  console.warn('Failed to parse SSE data:', dataStr);
                }
              }

              if (streamFinished) {
                break;
              }
            }
          } finally {
            clearInterval(idleWatcher);
            reader.releaseLock();
          }
        }
        clearInterval(idleWatcher);

        // If no SSE events were received, try parsing as JSON fallback
        if (!jsonPayload && !accumulatedOutput) {
          // Response might be plain JSON (non-streaming fallback)
          const text = sseBuffer || await response.text();
          try {
            jsonPayload = JSON.parse(text);
          } catch {
            accumulatedOutput = text;
          }
        }

        // Build final data from accumulated stream or JSON payload
        const finalText = jsonPayload?.response || jsonPayload?.text || jsonPayload?.output_text || jsonPayload?.message || accumulatedOutput || '';
        // Use output as reasoning fallback when no explicit reasoning was streamed (for non-o1/o3 models)
        const finalReasoning = accumulatedReasoning || jsonPayload?.reasoning || jsonPayload?.reasoning_output_text || accumulatedOutput || finalText;

        // Reset investigation state for ANSWER messages
        if (messageKind === "ANSWER" || jsonPayload?.kind === "ARTF") {
          setInvestigationInProgress(false);
          setInvestigationQuestionId(null);
        }

        // Final UI update
        setStreamingResponse(prev => prev ? {
          ...prev,
          output: finalText,
          reasoning: finalReasoning || 'No reasoning returned'
        } : prev);

        finalStreamData = {
          text: finalText,
          reasoning: finalReasoning,
          tokens: jsonPayload?.tokens,
          inputTokens: jsonPayload?.inputTokens,
          outputTokens: jsonPayload?.outputTokens,
          latencyMs: jsonPayload?.modelLatencyMs,
          model: jsonPayload?.model,
          userMessageId: jsonPayload?.userMessageId,
          kind: jsonPayload?.kind,
        };
        
        console.log('stream:done', { lenOutput: finalText.length, lenReasoning: (finalReasoning || '').length, model: jsonPayload?.model });
        console.log('stream:done:payload', {
          response: finalText.slice(0, 80) + (finalText.length > 80 ? '...' : ''),
          reasoning: (finalReasoning || '').slice(0, 80) + ((finalReasoning || '').length > 80 ? '...' : ''),
          userMessageId: jsonPayload?.userMessageId,
        });
      } catch (err) {
        if (err?.name === 'AbortError') {
          streamError = new Error('Request timed out after 5 minutes');
        } else {
          streamError = err;
        }
      } finally {
        clearTimeout(timeoutId);
        streamAbortRef.current = null;
      }

      if (streamError) {
        throw streamError;
      }

      if (isClientMessageCancelled(clientMessageId)) {
        return;
      }

      // Play success sound when artifact is created successfully
      if (isDeepThinking && !isClientMessageCancelled(clientMessageId)) {
        playSuccessSound();
      }

      // Handle investigation state based on response kind
      const responseKind = finalStreamData?.kind;
      
      if (messageKind === "ANSWER" || responseKind === "ARTF") {
        // Reset investigation state after artifact creation
        setInvestigationInProgress(false);
        setInvestigationQuestionId(null);
      } else if (responseKind === "QUESTION") {
        // Set investigation state when backend returns a QUESTION
        // This ensures the UI shows the investigation is in progress and locks the "Create Document" toggle
        const questionId = finalStreamData?.userMessageId;
        // The QUESTION message ID will be in refreshedMessages after we fetch them
      }
      
      // Refresh messages from database to get the actual saved messages with all metadata
      // Then explicitly select the new artifact if one was created (ANSWER leads to ARTF)
      const { data: refreshedMessages } = await supabase
        .from('messages_vr')
        .select('*')
        .eq('conversation_id', currentConversation.id)
        .order('created_at', { ascending: true });
      
      if (refreshedMessages) {
        setMessages(refreshedMessages as Message[]);
        pruneCancelledMessages(refreshedMessages as Message[]);
        
        // Handle different response kinds
        if (responseKind === "ARTF") {
          // Find and select the newly created artifact.
          // ANSWER can now return a CHAT fallback, so do not infer ARTF from request kind alone.
          const lastAssistantArtf = [...refreshedMessages]
            .reverse()
            .find(m =>
              !m.is_user &&
              m.kind === "ARTF" &&
              (!finalStreamData?.userMessageId || m.parent_message_id === finalStreamData.userMessageId)
            );
          
          if (lastAssistantArtf) {
            setSelectedArtifactId(lastAssistantArtf.id);
            trackEvent('artifact_generated', {
              lens_id: lastAssistantArtf.thinking_method_id || requestThinkingMethodId,
              lens_type: boardMode ? 'board' : 'single',
              artifact_kind: 'ARTF',
              status: 'success',
            });
            
            // Trigger PDF generation from frontend
            void triggerPdfGeneration(lastAssistantArtf.id, user?.id)
              .then(async (pdfResult) => {
                if (!pdfResult?.followUpMessageId || isClientMessageCancelled(clientMessageId)) {
                  return;
                }

                const { data: messagesAfterPdf, error: refreshAfterPdfError } = await supabase
                  .from('messages_vr')
                  .select('*')
                  .eq('conversation_id', currentConversation.id)
                  .order('created_at', { ascending: true });

                if (refreshAfterPdfError) {
                  console.warn('[PDF] Follow-up inserted but message refresh failed:', refreshAfterPdfError);
                  return;
                }

                if (messagesAfterPdf) {
                  setMessages(messagesAfterPdf as Message[]);
                  pruneCancelledMessages(messagesAfterPdf as Message[]);
                }
              })
              .catch((refreshErr) => {
                console.warn('[PDF] Follow-up refresh failed:', refreshErr);
              });
          }
        } else if (responseKind === "QUESTION") {
          // Find the QUESTION message and set investigation state
          const lastAssistantQuestion = [...refreshedMessages]
            .reverse()
            .find(m => !m.is_user && m.kind === "QUESTION");
          
          if (lastAssistantQuestion) {
            setInvestigationInProgress(true);
            setInvestigationQuestionId(lastAssistantQuestion.id);
          }
        }
      }
      
      // Refresh credit balance after every assistant response (covers CHAT/ARTF/QUESTION).
      // Realtime on profile_attributes can be flaky, so we always pull fresh value.
      refreshCredits();

      setStreamingResponse(null);

    } catch (error) {
      console.error('Error sending message:', error);
      streamAbortRef.current?.abort();
      setStreamingResponse(null);
      if (isClientMessageCancelled(clientMessageId)) {
        return;
      }

      // Mark the request as cancelled so the UI doesn't stay stuck
      markMessageCancelled(clientMessageId);
      setNewMessage(messageContent);

      playErrorSound();
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
      // Refresh data to show correct state
      await fetchData();
    } finally {
      setStreamingResponse(null);
      setUseDeepThinkingMode(false);
      setSending(false);
      setDeepThinking(false);
      // Auto-focus the input after response completes
      setTimeout(() => textareaRef.current?.focus(), 100);
      // NOTE: Do NOT clear activeClientMessageIdRef here!
      // The ref must persist until the next message is sent (line 599) to ensure
      // the user message remains visible during React re-renders after setMessages().
      // Clearing it here causes a race condition where the message gets filtered out
      // because isActiveOptimisticMessage becomes false before the render completes.
    }
  };

  const handleCancelRequest = () => {
    const clientMessageId =
      pendingResponseMessage?.client_message_id || activeClientMessageIdRef.current;

    if (!clientMessageId) return;

    markMessageCancelled(clientMessageId);
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    setStreamingResponse(null);

    // NOTE: Do NOT clear activeClientMessageIdRef here either.
    // It will be cleared at the start of the next message send.

    setSending(false);
    setDeepThinking(false);
    setUseDeepThinkingMode(false);
    
    // Reset investigation state if cancelling during investigation
    if (investigationInProgress) {
      setInvestigationInProgress(false);
      setInvestigationQuestionId(null);
    }

    toast({
      title: "Request cancelled",
      description: "Stopped generating the current response.",
    });
  };

  const triggerDeepThinking = async () => {
    if (!messages.length || deepThinking || sending) return;
    
    // Get the last user message
    const lastUserMessage = [...messages].reverse().find(m => m.is_user);
    if (!lastUserMessage) return;

    setNewMessage(lastUserMessage.content);
    await sendMessage(true);
  };

  const deleteConversation = async () => {
    if (!conversation || deletingConversation) return;
    const currentMethodId = selectedLensIds[0] ?? conversation.thinking_method_id ?? thinkingMethod?.id;
    if (!currentMethodId) return;

    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(boardMode ? 'Delete this board and its messages?' : 'Delete this conversation and its documents?');
      if (!confirmed) return;
    }

    const conversationId = conversation.id;
    const previousArtifactId = selectedArtifactId;
    const previousEditingArtifactId = editingArtifactId;
    const previousDocumentInfo = documentInfo;

    setDeletingConversation(true);
    setSelectedArtifactId(null);
    setEditingArtifactId(null);
    setDocumentInfo(null);

    try {
      if (conversationId) {
        const { data: deletedConversation, error } = await supabase
          .from('conversations')
          .delete()
          .eq('id', conversationId)
          .select('id')
          .maybeSingle();

        if (error) throw error;
        if (!deletedConversation) throw new Error('Conversation was not deleted');
      }

      // Database triggers retain durable cleanup work for every cascaded document.

      // Clear the conversation state and messages
      setConversation(null);
      setMessages([]);
      updateContextDocuments([]);
      clearCancelledMessages();
      setInvestigationInProgress(false);
      setInvestigationQuestionId(null);
      setUseDeepThinkingMode(false);

      if (boardMode) {
        setSelectedLensIds([]);
        setDocumentLensId(null);
        navigate('/expert-boards', { replace: true });
        toast({
          title: "Success",
          description: "Board deleted successfully",
        });
        return;
      }

      // Set up a "pending" placeholder conversation in memory so the user can
      // keep chatting. We do NOT insert into the DB yet — that happens lazily
      // on the first user message in sendMessage().
      const pendingConversation: Conversation = {
        id: '',
        thinking_method_id: currentMethodId,
        pending: true,
      };
      setConversation(pendingConversation);
      setSelectedLensIds([currentMethodId]);
      setDocumentLensId(currentMethodId);
      navigate(`/chat/new?method=${currentMethodId}`, { replace: true });

      // Show the greeting locally without persisting it.
      let newMessages: Message[] = [];
      if (currentMethodId) {
        const { data: greetingAttr } = await supabase
          .from('thinking_method_attributes')
          .select('attribute_value')
          .eq('thinking_method_id', currentMethodId)
          .eq('attribute_name', 'GREETING')
          .maybeSingle();

        if (greetingAttr?.attribute_value) {
          newMessages = [{
            id: `${LOCAL_GREETING_ID_PREFIX}${currentMethodId}`,
            content: greetingAttr.attribute_value,
            is_user: false,
            created_at: new Date().toISOString(),
            kind: 'CHAT',
            thinking_method_id: currentMethodId,
          }];
        }
      }
      setMessages(newMessages);

      toast({
        title: "Success",
        description: "Conversation deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      playErrorSound();
      setSelectedArtifactId(previousArtifactId);
      setEditingArtifactId(previousEditingArtifactId);
      setDocumentInfo(previousDocumentInfo);
      toast({
        title: "Error",
        description: boardMode ? "Failed to delete board" : "Failed to delete conversation",
        variant: "destructive",
      });
    } finally {
      setDeletingConversation(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (isInputDisabled) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(useDeepThinkingMode);
    }
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return format(date, 'MMM d, yyyy h:mm a');
  };

  const formatResponseTime = (ms?: number) => {
    if (!ms) return null;
    if (ms < 1000) return `${ms}ms`;
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds}s`;
  };

  const messageHasQuestionAncestor = (message: Message | null | undefined): boolean => {
    if (!message?.parent_message_id) return false;

    const messageById = new Map<string, Message>();
    for (const item of messages) messageById.set(item.id, item);
    for (const item of activePath) messageById.set(item.id, item);

    const seen = new Set<string>();
    let parentId: string | null | undefined = message.parent_message_id;
    let hops = 0;

    while (parentId && !seen.has(parentId) && hops < 24) {
      seen.add(parentId);
      const parent = messageById.get(parentId);
      if (!parent) break;
      if (!parent.is_user && parent.kind === "QUESTION") return true;
      parentId = parent.parent_message_id;
      hops += 1;
    }

    return false;
  };

  const determineEditedUserMessageKind = (
    message: Message | null | undefined,
    createDocumentSwitchOn: boolean,
  ): "CHAT" | "ARTF" | "ANSWER" => {
    if (!createDocumentSwitchOn) return "CHAT";
    if (message?.kind === "ANSWER") return "ANSWER";
    if (message?.kind === "ARTF") return "ARTF";
    if (messageHasQuestionAncestor(message)) return "ANSWER";

    const lastMessage = activePath.length > 0 ? activePath[activePath.length - 1] : null;
    return determineMessageKind(true, lastMessage) === "ANSWER" ? "ANSWER" : "ARTF";
  };

  const startEdit = (message: Message) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
    setEditDeepThinkingMode(
      !documentSwitchLocked && (message.kind === "ARTF" || message.kind === "ANSWER")
    );
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
    setEditDeepThinkingMode(false);
  };

  const saveEdit = async (messageId: string) => {
    if (!editContent.trim() || !conversation) return;

    // Clear artifact panel when editing message
    setSelectedArtifactId(null);
    autoScrollEnabledRef.current = true;
    
    // Determine message kind from the edited row lineage, not from the current
    // leaf. Edits to clarification answers must stay ANSWER so the backend
    // skips fresh scope/clarification and regenerates the artifact directly.
    const editedMessage =
      messages.find(m => m.id === messageId) ??
      activePath.find(m => m.id === messageId) ??
      null;
    const messageKind = determineEditedUserMessageKind(editedMessage, editDeepThinkingMode);
    const useDeepThinking = messageKind === "ARTF" || messageKind === "ANSWER";
    const editMethodOrder = uniqueConversationMethodIds(
      selectedLensIds.length > 0
        ? selectedLensIds
        : [conversation.thinking_method_id ?? thinkingMethod?.id]
    );
    const editPrimaryLensId = editMethodOrder[0] ?? conversation.thinking_method_id ?? thinkingMethod?.id ?? null;
    const selectedEditDocumentLensId =
      documentLensId && editMethodOrder.includes(documentLensId)
        ? documentLensId
        : null;
    const editDocumentLensId = resolveRegenerationLensId({
      editedMessage,
      messages,
      activePath,
      selectedDocumentLensId: selectedEditDocumentLensId,
      conversationPrimaryLensId: editPrimaryLensId,
    });
    const editRequestLensId = useDeepThinking ? editDocumentLensId : editPrimaryLensId;

    if (useDeepThinking && documentSwitchLocked) {
      playErrorSound();
      promptDocumentUpgrade();
      return;
    }

    setSending(true);
    
    cancelEdit();

    try {
      // Refresh token before making edge function call
      if (useDeepThinking) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          playErrorSound();
          toast({
            title: "Session Expired",
            description: "Please log out and log back in to continue",
            variant: "destructive",
          });
          setSending(false);
          return;
        }
      }
      // Optimistically update UI: show edited question and remove old assistant replies
      const updatedText = editContent.trim();
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === messageId);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], content: updatedText, kind: messageKind };
        // Remove contiguous assistant messages right after this user message
        const j = idx + 1;
        while (j < next.length && !next[j].is_user) {
          next.splice(j, 1);
        }
        return next;
      });

      // Load the original message details to reuse lineage metadata
      const { data: originalMessage, error: originalErr } = await supabase
        .from('messages_vr')
        .select('parent_message_id, version, client_message_id')
        .eq('id', messageId)
        .single();
      if (originalErr) throw originalErr;

      // Compute next user version number for this lineage
      const { data: existing, error: vErr } = await supabase
        .from('messages_vr')
        .select('version')
        .eq('conversation_id', conversation.id)
        .eq('is_user', true)
        .eq('parent_message_id', messageId)
        .order('version', { ascending: false })
        .limit(1);
      if (vErr) throw vErr;

      const maxChildVersion = (existing?.[0]?.version as number | undefined) ?? -1;
      const currentVersion = (originalMessage?.version as number | undefined) ?? 0;
      const nextVersion = Math.max(currentVersion, maxChildVersion) + 1;

      // Create a new user message version row linked to the original
      const { data: insertedUserVersion, error: insertErr } = await supabase
        .from('messages_vr')
        .insert({
          conversation_id: conversation.id,
          content: updatedText,
          is_user: true,
          thinking_method_id: null,
          parent_message_id: messageId,
          version: nextVersion,
          client_message_id: originalMessage?.client_message_id ?? null,
          kind: messageKind,
        })
        .select('id')
        .single();
      if (insertErr) throw insertErr;

      const newUserMessageId = insertedUserVersion?.id ?? messageId;

      // Regenerate assistant response, linked to the newly created version
      // Use userMessageParentId (what the edge function expects) and pass clientMessageId
      // so the edge function finds the already-inserted message and skips re-insertion
      const editClientMessageId = `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Update the already-inserted message with the clientMessageId for dedup
      await supabase
        .from('messages_vr')
        .update({ client_message_id: editClientMessageId })
        .eq('id', newUserMessageId);

      const invokeEdit = async () =>
        await supabase.functions.invoke('chat-with-thinking-method', {
          body: {
            messageId,
            newContent: updatedText,
            thinkingMethodId: editRequestLensId,
            dbConversationId: conversation.id,
            isEdit: true,
            kind: messageKind,
            userMessageParentId: messageId,
            clientMessageId: editClientMessageId,
            ...(activeContextDocumentIds.length > 0 ? { contextDocumentIds: activeContextDocumentIds } : {}),
          },
        });

      let invokeError = null;
      let invokeData = null;
      for (let attempt = 1; attempt <= 2; attempt++) {
        const { data, error } = await invokeEdit();
        if (!error) {
          invokeData = data;
          break;
        }
        invokeError = error;
        // Retry once on transient network failures
        if (String(error?.name).includes('FunctionsFetchError') || String(error?.message).includes('Failed to fetch')) {
          await new Promise((r) => setTimeout(r, 600));
          continue;
        }
        break;
      }

      if (invokeError) throw invokeError;

      // Play success sound when artifact is created successfully via edit
      if (useDeepThinking) {
        playSuccessSound();
      }

      // Refresh messages to get new assistant reply
      await fetchData();
      refreshCredits();
      
      toast({
        title: "Success",
        description: "Message edited and responses regenerated",
      });
    } catch (error) {
      console.error('Error editing message:', error);
      playErrorSound();
      toast({
        title: "Error",
        description: "Failed to edit message",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleEditKeyPress = (e: React.KeyboardEvent, messageId: string) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      saveEdit(messageId);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleActionItemSelect = (item: NextMoveActionItem) => {
    const nextText = item.question || item.description || item.title || item.raw;
    if (!nextText) return;

    setNewMessage(nextText);
    setSelectedArtifactId(null);

    if (item.type === 'create_artifact') {
      if (documentSwitchLocked) {
        promptDocumentUpgrade();
        setUseDeepThinkingMode(false);
        return;
      }
      setUseDeepThinkingMode(true);
    }
    if (item.type === 'generic_question') {
      setUseDeepThinkingMode(false);
    }

    setTimeout(() => {
      textareaRef.current?.focus();
      const length = nextText.length;
      textareaRef.current?.setSelectionRange(length, length);
    }, 0);
  };

  const deleteMessage = async (message: Message) => {
    if (!conversation) return;

    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Delete this message and related responses?');
      if (!confirmed) return;
    }

    const previousMessages = [...messages];
    const previousArtifactId = selectedArtifactId;

    const messagesById = new Map(previousMessages.map((m) => [m.id, m]));

    // If deleting a user edit from a chain, re-parent its edit children
    // to preserve the remaining versions in the chain.
    const editChildrenToReparent: Message[] = [];
    if (message.is_user && message.kind === 'CHAT') {
      previousMessages.forEach((m) => {
        if (
          m.parent_message_id === message.id &&
          m.is_user &&
          m.kind === 'CHAT'
        ) {
          editChildrenToReparent.push(m);
        }
      });
    }

    const idsToRemove = new Set<string>();
    const reparentedIds = new Set(editChildrenToReparent.map(m => m.id));
    const collectDescendants = (targetId: string) => {
      idsToRemove.add(targetId);
      previousMessages.forEach((m) => {
        if (m.parent_message_id === targetId && !idsToRemove.has(m.id) && !reparentedIds.has(m.id)) {
          collectDescendants(m.id);
        }
      });
    };
    collectDescendants(message.id);

    let cancelledActiveMessage = false;
    idsToRemove.forEach((id) => {
      const target = messagesById.get(id);
      if (target?.is_user) {
        markMessageCancelled(target.client_message_id);
        if (
          target.client_message_id &&
          target.client_message_id === activeClientMessageIdRef.current
        ) {
          cancelledActiveMessage = true;
        }
      }
    });

    if (cancelledActiveMessage) {
      setSending(false);
      setDeepThinking(false);
      setUseDeepThinkingMode(false);
      activeClientMessageIdRef.current = null;
    }

    if (selectedArtifactId && idsToRemove.has(selectedArtifactId)) {
      setSelectedArtifactId(null);
    }

    if (editingMessageId && idsToRemove.has(editingMessageId)) {
      cancelEdit();
    }

    // Reset investigation state if deleting the question message
    if (investigationQuestionId && idsToRemove.has(investigationQuestionId)) {
      setInvestigationInProgress(false);
      setInvestigationQuestionId(null);
    }

    setDeletingMessageId(message.id);

    // Optimistically update local state: re-parent edit children, then remove deleted messages
    const newParentId = message.parent_message_id || null;
    setMessages((prev) =>
      prev
        .map((m) => {
          if (reparentedIds.has(m.id)) {
            return { ...m, parent_message_id: newParentId ?? undefined };
          }
          return m;
        })
        .filter((m) => !idsToRemove.has(m.id))
    );

    try {
      // Re-parent edit children in the DB before deleting
      if (editChildrenToReparent.length > 0) {
        const { error: reparentError } = await supabase
          .from('messages_vr')
          .update({ parent_message_id: newParentId })
          .in('id', editChildrenToReparent.map(m => m.id));
        if (reparentError) throw reparentError;
      }

      // Delete the message and its non-edit descendants from the DB
      const idsArray = Array.from(idsToRemove);
      const { error } = await supabase
        .from('messages_vr')
        .delete()
        .in('id', idsArray);
      if (error) throw error;

      toast({
        title: "Message deleted",
        description: "The message and related responses were removed.",
      });
      await fetchData();
    } catch (error) {
      console.error('Error deleting message:', error);
      playErrorSound();
      setMessages(previousMessages);
      setSelectedArtifactId(previousArtifactId);
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive",
      });
    } finally {
      setDeletingMessageId(null);
    }
  };



  // Get all artifacts sorted by creation time (newest first)
  const allArtifacts = messages
    .filter(m => !m.is_user && m.kind === "ARTF")
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const lastVisibleMsg = activePath.length > 0 ? activePath[activePath.length - 1] : undefined;
  const lastArtifactMessageId =
    lastVisibleMsg && !lastVisibleMsg.is_user && lastVisibleMsg.kind === "ARTF" ? lastVisibleMsg.id : null;

  useEffect(() => {
    const currentArtifactIds = allArtifacts.map(a => a.id);

    if (!artifactsInitializedRef.current) {
      previousArtifactIdsRef.current = currentArtifactIds;
      artifactsInitializedRef.current = true;
      setSelectedArtifactId(lastArtifactMessageId);
      return;
    }

    const previousArtifactIds = previousArtifactIdsRef.current;
    const newlyCreatedId = currentArtifactIds.find(id => !previousArtifactIds.includes(id));

    if (newlyCreatedId && newlyCreatedId === lastArtifactMessageId) {
      setSelectedArtifactId(newlyCreatedId);
    } else if (selectedArtifactId && !currentArtifactIds.includes(selectedArtifactId)) {
      setSelectedArtifactId(null);
    }

    previousArtifactIdsRef.current = currentArtifactIds;
  }, [allArtifacts, selectedArtifactId, lastArtifactMessageId]);

  // Determine artifact panel state
  const selectedArtifact = selectedArtifactId
    ? allArtifacts.find(a => a.id === selectedArtifactId) || null
    : null;

  const isArtifactPanelActive = !!selectedArtifact;

  useEffect(() => {
    if (!selectedArtifact || viewedArtifactIdsRef.current.has(selectedArtifact.id)) return;
    viewedArtifactIdsRef.current.add(selectedArtifact.id);
    trackEvent('artifact_viewed', {
      lens_id: selectedArtifact.thinking_method_id || thinkingMethod?.id || 'unknown',
      lens_type: boardMode ? 'board' : 'single',
      artifact_kind: 'ARTF',
      page_path: location.pathname,
    });
  }, [boardMode, location.pathname, selectedArtifact, thinkingMethod?.id]);

  // Editing artifact context
  const editingArtifact = editingArtifactId
    ? allArtifacts.find(a => a.id === editingArtifactId) || null
    : null;

  const startEditingArtifact = (artifactId: string) => {
    if (documentSwitchLocked) {
      promptDocumentUpgrade();
      return;
    }
    setEditingArtifactId(artifactId);
    setMobilePanel('chat');
  };

  useEffect(() => {
    if (editingArtifactId && mobilePanel === 'chat') textareaRef.current?.focus();
  }, [editingArtifactId, mobilePanel]);

  const cancelEditingArtifact = () => {
    setEditingArtifactId(null);
  };

  const groupedAvailableLenses = useMemo(() => {
    return groupByIndustry(availableLenses);
  }, [availableLenses]);

  const selectedLensIdSet = useMemo(() => new Set(selectedLensIds), [selectedLensIds]);
  const selectedLensDetails = useMemo(() => {
    return selectedLensIds.map((id) => {
      const visibleLens = availableLenses.find((lens) => lens.id === id);
      if (visibleLens) return visibleLens;
      if (thinkingMethod?.id === id) {
        return {
          ...thinkingMethod,
          status: thinkingMethod.status || 'published',
        } as LensOption;
      }
      return {
        id,
        name: lensNameById[id] || `Lens ${id.slice(0, 8)}`,
        author: 'Unknown',
        bio: '',
        status: 'published',
      } as LensOption;
    });
  }, [availableLenses, lensNameById, selectedLensIds, thinkingMethod]);

  const boardDraftLensIdSet = useMemo(() => new Set(boardDraftLensIds), [boardDraftLensIds]);
  const boardDraftLensDetails = useMemo(() => {
    return boardDraftLensIds.map((id) => {
      const visibleLens = availableLenses.find((lens) => lens.id === id);
      if (visibleLens) return visibleLens;
      if (thinkingMethod?.id === id) {
        return {
          ...thinkingMethod,
          status: thinkingMethod.status || 'published',
        } as LensOption;
      }
      return {
        id,
        name: lensNameById[id] || `Lens ${id.slice(0, 8)}`,
        author: 'Unknown',
        bio: '',
        status: 'published',
      } as LensOption;
    });
  }, [availableLenses, boardDraftLensIds, lensNameById, thinkingMethod]);

  const activeDocumentLensId = documentLensId && selectedLensIdSet.has(documentLensId)
    ? documentLensId
    : selectedLensIds[0] ?? thinkingMethod?.id ?? '';

  const activeDocumentLens = selectedLensDetails.find((lens) => lens.id === activeDocumentLensId)
    ?? selectedLensDetails[0]
    ?? thinkingMethod;

  if (guestSessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center space-y-4">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Lock className="h-5 w-5" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">Unable to start guest chat</h1>
            <p className="text-sm leading-6 text-muted-foreground">{guestSessionError}</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              onClick={() => {
                guestSessionRequestInFlightRef.current = false;
                setGuestSessionError('');
                setLoading(true);
                setGuestSessionAttempt((current) => current + 1);
              }}
            >
              Try again
            </Button>
            <Button variant="outline" onClick={() => navigate('/lenses')}>
              Browse experts
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 text-primary mx-auto animate-spin" />
          <p className="text-muted-foreground">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (!thinkingMethod) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Expert not found</p>
          <Button onClick={() => navigate('/')}>Go Back</Button>
        </div>
      </div>
    );
  }

  const initials = thinkingMethod.name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const boardName = boardMode
    ? getConversationBoardName(conversation?.openai_conversation_id)
    : null;
  const userDisplayName =
    (user?.user_metadata as { name?: string; full_name?: string } | undefined)?.name
      || (user?.user_metadata as { name?: string; full_name?: string } | undefined)?.full_name
      || user?.email
      || 'You';
  const userAvatarUrl =
    (user?.user_metadata as { avatar_url?: string; picture?: string } | undefined)?.avatar_url
      || (user?.user_metadata as { avatar_url?: string; picture?: string } | undefined)?.picture
      || undefined;
  const userInitials = (userDisplayName || 'You')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const lensById = new Map(selectedLensDetails.map((lens) => [lens.id, lens]));


  const copyArtifactToClipboard = () => {
    if (isAnonymous) {
      openConvertPrompt();
      return;
    }
    if (selectedArtifact) {
      navigator.clipboard.writeText(selectedArtifact.content);
      trackEvent('copy_clicked', {
        lens_id: selectedArtifact.thinking_method_id || thinkingMethod?.id || 'unknown',
        source: 'artifact',
        artifact_kind: 'ARTF',
        status: 'success',
      });
      toast({
        title: "Copied to clipboard",
        description: "Artifact content has been copied.",
      });
    }
  };

  const sanitizeFilenamePart = (value: string) => {
    return value.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
  };

  const getConversationDescription = () => {
    const firstUserMessage = messages.find(m => m.is_user)?.content || 'conversation';
    const trimmed = firstUserMessage.trim();
    const truncated = trimmed.length > 50 ? `${trimmed.substring(0, 50).trim()}...` : trimmed;
    const sanitized = sanitizeFilenamePart(truncated);
    return sanitized || 'conversation';
  };

  const buildDownloadFilename = (extension: string) => {
    const sanitizedMethodName = sanitizeFilenamePart(thinkingMethod.name) || 'Conversation';
    const description = getConversationDescription();

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().substring(0, 5).replace(':', '.');

    return `${sanitizedMethodName} - ${description} - ${dateStr} ${timeStr}.${extension}`;
  };

  const downloadAsMarkdown = () => {
    if (isAnonymous) {
      openConvertPrompt();
      return;
    }
    if (!selectedArtifact) return;
    trackEvent('download_clicked', {
      lens_id: selectedArtifact.thinking_method_id || thinkingMethod?.id || 'unknown',
      source: 'artifact',
      format: 'md',
      artifact_kind: 'ARTF',
      status: 'started',
    });

    const content = typeof selectedArtifact.content === 'string'
      ? selectedArtifact.content
      : JSON.stringify(selectedArtifact.content);

    // Use document title if available, otherwise build filename
    const filename = activeDocumentInfo?.title
      ? `${sanitizeFilenamePart(activeDocumentInfo.title)}.md`
      : buildDownloadFilename('md');

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Downloaded",
      description: "Markdown file has been downloaded.",
    });
  };

  const downloadAsPDF = () => {
    if (isAnonymous) {
      openConvertPrompt();
      return;
    }
    if (!selectedArtifact || !documentPdf?.pdfUrl) {
      toast({
        title: "PDF not available",
        description: "The PDF document is not ready for download yet.",
        variant: "destructive",
      });
      return;
    }
    trackEvent('download_clicked', {
      lens_id: selectedArtifact.thinking_method_id || thinkingMethod?.id || 'unknown',
      source: 'artifact',
      format: 'pdf',
      artifact_kind: 'ARTF',
      status: 'started',
    });
    
    const link = document.createElement('a');
    link.href = documentPdf.pdfUrl;
    // Use document title for filename
    const filename = `${activeDocumentInfo?.title || documentPdf.title || 'document'}.pdf`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Downloaded",
      description: "PDF file has been downloaded.",
    });
  };

  const getArtifactTitle = (content: string) => {
    const firstLine = content.split('\n').find(line => line.trim().length > 0) || 'Artifact';
    return firstLine.replace(/^#+\s*/, '').trim().substring(0, 80) + (firstLine.length > 80 ? '...' : '');
  };

  const isStreaming = !!streamingResponse;
  const waitingForFirstTokens =
    isStreaming &&
    (!streamingResponse.output || streamingResponse.output.length === 0) &&
    (!streamingResponse.reasoning || streamingResponse.reasoning.length === 0);
  const showLoadingState =
    ((sending || deepThinking || hasPendingResponse) && !isStreaming) ||
    waitingForFirstTokens;
  const showCancelButton = showLoadingState || isStreaming;
  const canCancelRequest = !!(
    pendingResponseMessage?.client_message_id ?? activeClientMessageIdRef.current
  );
  const loadingLabel = isStreaming
    ? 'Streaming...'
    : (deepThinking || pendingResponseMessage?.kind === "ARTF")
      ? 'Creating...'
      : 'Thinking...';
  const isInputDisabled = sending || deepThinking || hasPendingResponse || isStreaming || switchingLens;
  const lensSwitchDisabled = isInputDisabled || !!editingMessageId || switchingLens;
  const trimmedBoardCreationName = boardCreationNameDraft.trim();

  const applyPendingMethodOrder = async (nextIds: string[]) => {
    const firstLens = availableLenses.find((lens) => lens.id === nextIds[0]) ?? thinkingMethod;
    if (!firstLens) return;

    const pendingConversation: Conversation = {
      id: '',
      thinking_method_id: nextIds[0],
      openai_conversation_id: buildOptionalConversationMethodsPayload(nextIds),
      pending: true,
    };
    setSelectedLensIds(nextIds);
    setDocumentLensId((prev) => prev && nextIds.includes(prev) ? prev : nextIds[0]);
    setConversation(pendingConversation);
    conversationRef.current = pendingConversation;
    setThinkingMethod(firstLens);
    setLensNameById((prev) => ({
      ...prev,
      ...Object.fromEntries(
        nextIds
          .map((id) => availableLenses.find((lens) => lens.id === id))
          .filter((lens): lens is LensOption => !!lens)
          .map((lens) => [lens.id, lens.name]),
      ),
      [firstLens.id]: firstLens.name,
    }));
    navigate(`/chat/new?method=${nextIds[0]}`, { replace: true });
  };

  const persistConversationMethods = async (
    nextIdsInput: string[],
    options: { requireBoard?: boolean; targetSurface?: 'chat' | 'board'; boardName?: string | null } = {},
  ) => {
    if (switchingLensRef.current || lensSwitchDisabled || !thinkingMethod) return;
    const targetSurface = options.targetSurface ?? (boardMode ? 'board' : 'chat');
    const nextIds = uniqueConversationMethodIds(nextIdsInput);
    const boardNameForCreation = targetSurface === 'board' && !boardMode
      ? (options.boardName ?? '').trim()
      : undefined;

    if (nextIds.length === 0) {
      toast({
        title: "At least one lens is required",
        description: "A conversation must keep one active lens.",
        variant: "destructive",
      });
      return;
    }
    if (options.requireBoard && nextIds.length < 2) {
      toast({
        title: "Choose one more Expert",
        description: "An Expert Board needs at least two Experts.",
        variant: "destructive",
      });
      return;
    }
    if (boardNameForCreation !== undefined && boardNameForCreation.length === 0) {
      toast({
        title: "Board name required",
        description: "Enter a name for the Expert Board.",
        variant: "destructive",
      });
      return;
    }
    if (nextIds.length > MAX_CONFERENCE_LENSES) {
      toast({
        title: "Board limit reached",
        description: `A Board can have up to ${MAX_CONFERENCE_LENSES} Experts.`,
        variant: "destructive",
      });
      return;
    }

    if (!conversation || conversation.pending || !conversation.id) {
      if (targetSurface === 'board') {
        switchingLensRef.current = true;
        setSwitchingLens(true);
        setSelectedArtifactId(null);
        try {
          const primaryConversationLensId = nextIds[0];
          const persisted = await persistPendingConversation({
            currentConversation: conversation ?? {
              id: '',
              thinking_method_id: primaryConversationLensId,
              pending: true,
            },
            activeMethodOrder: nextIds,
            primaryConversationLensId,
            targetSurface: 'board',
            boardName: boardNameForCreation,
          });
          const firstLens = availableLenses.find((lens) => lens.id === nextIds[0]) ?? thinkingMethod;
          setConversation({
            ...persisted.conversation,
            thinking_method_id: nextIds[0],
          });
          setSelectedLensIds(nextIds);
          setDocumentLensId((prev) => prev && nextIds.includes(prev) ? prev : nextIds[0]);
          setThinkingMethod(firstLens);
          setLensNameById((prev) => ({
            ...prev,
            ...Object.fromEntries(
              nextIds
                .map((id) => availableLenses.find((lens) => lens.id === id))
                .filter((lens): lens is LensOption => !!lens)
                .map((lens) => [lens.id, lens.name]),
            ),
          }));
          setLensPickerOpen(false);
          setSelectedLensIndustry(null);
          if (!boardMode) {
            setBoardCreationNameDraft('');
          }
          toast({
            title: "Expert Board created",
            description: `${nextIds.length} Experts will answer each turn.`,
          });
        } catch (error) {
          console.error('Error creating Expert Board:', error);
          playErrorSound();
          toast({
            title: "Error",
            description: "Failed to create Expert Board",
            variant: "destructive",
          });
        } finally {
          switchingLensRef.current = false;
          setSwitchingLens(false);
        }
        return;
      }

      await applyPendingMethodOrder(nextIds);
      setLensPickerOpen(false);
      setSelectedLensIndustry(null);
      return;
    }

    const leafMessage = activePath.length > 0 ? activePath[activePath.length - 1] : null;
    const parentMessageId = leafMessage && !isLocalGreetingId(leafMessage.id) ? leafMessage.id : null;

    switchingLensRef.current = true;
    setSwitchingLens(true);
    setSelectedArtifactId(null);
    try {
      const { data, error } = await supabase.functions.invoke('manage-conversation-methods', {
        body: {
          conversationId: conversation.id,
          methodIds: nextIds,
          parentMessageId,
          name: boardNameForCreation,
        },
      });

      if (error) throw error;

      const firstLens = availableLenses.find((lens) => lens.id === nextIds[0]) ?? thinkingMethod;
      const updatedConversation: Conversation = {
        ...conversation,
        ...(data?.conversation || {}),
        thinking_method_id: nextIds[0],
        pending: false,
      };
      setConversation(updatedConversation);
      conversationRef.current = updatedConversation;
      setSelectedLensIds(nextIds);
      setDocumentLensId((prev) => prev && nextIds.includes(prev) ? prev : nextIds[0]);
      setThinkingMethod(firstLens);
      setLensNameById((prev) => ({
        ...prev,
        ...Object.fromEntries((data?.lenses || []).map((lens) => [lens.id, lens.name])),
        [firstLens.id]: firstLens.name,
      }));
      if (Array.isArray(data?.messages) && data.messages.length > 0) {
        setMessages((prev) => [...prev, ...(data.messages as Message[])]);
      } else {
        await fetchData();
      }
      setInvestigationInProgress(false);
      setInvestigationQuestionId(null);
      setLensPickerOpen(false);
      setSelectedLensIndustry(null);
      if (!boardMode) {
        setBoardCreationNameDraft('');
      }
      if (targetSurface === 'board') {
        navigate(`/board/${updatedConversation.id}${applicationsOriginSearch}`, { replace: true });
      } else {
        navigate(`/chat/${updatedConversation.id}${applicationsOriginSearch}`, { replace: true });
      }
      toast({
        title: targetSurface === 'board'
          ? (boardMode ? "Expert Board saved" : "Expert Board created")
          : "Lenses updated",
        description: targetSurface === 'board'
          ? (nextIds.length > 1 ? `${nextIds.length} Experts will answer each turn.` : `Now using ${firstLens.name}.`)
          : (nextIds.length > 1 ? `${nextIds.length} lenses will answer each chat turn.` : `Now using ${firstLens.name}.`),
      });
    } catch (error) {
      console.error('Error updating conversation lenses:', error);
      playErrorSound();
      toast({
        title: "Error",
        description: "Failed to update lenses",
        variant: "destructive",
      });
    } finally {
      switchingLensRef.current = false;
      setSwitchingLens(false);
    }
  };

  const addConferenceLens = (lens: LensOption) => {
    setBoardDraftLensIds((prev) => {
      const next = uniqueConversationMethodIds([...prev, lens.id]);
      return next.length > MAX_CONFERENCE_LENSES ? prev : next;
    });
  };

  const removeConferenceLens = (lensId: string) => {
    setBoardDraftLensIds((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((id) => id !== lensId);
    });
  };

  const moveConferenceLens = (lensId: string, direction: -1 | 1) => {
    const index = boardDraftLensIds.indexOf(lensId);
    const targetIndex = index + direction;
    if (index === -1 || targetIndex < 0 || targetIndex >= boardDraftLensIds.length) return;
    const nextIds = [...boardDraftLensIds];
    [nextIds[index], nextIds[targetIndex]] = [nextIds[targetIndex], nextIds[index]];
    setBoardDraftLensIds(nextIds);
  };

  const openExpertBoardPicker = () => {
    const primaryLensId = selectedLensIds[0] ?? thinkingMethod?.id ?? conversation?.thinking_method_id ?? null;
    const startingIds = boardMode
      ? uniqueConversationMethodIds(selectedLensIds.length > 0 ? selectedLensIds : [primaryLensId])
      : uniqueConversationMethodIds([primaryLensId]);
    setBoardDraftLensIds(startingIds);
    if (!boardMode) {
      setBoardCreationNameDraft('');
    }
    setSelectedLensIndustry(null);
    setLensPickerOpen(true);
  };

  const saveExpertBoardMode = () => {
    void persistConversationMethods(boardDraftLensIds, {
      requireBoard: true,
      targetSurface: 'board',
      boardName: boardMode ? undefined : boardCreationNameDraft,
    });
  };

  const startRenamingBoard = () => {
    if (!boardMode || !conversation || conversation.pending) return;
    setBoardNameDraft(boardName ?? '');
    setRenamingBoard(true);
  };

  const cancelRenamingBoard = () => {
    setRenamingBoard(false);
    setBoardNameDraft('');
  };

  const saveBoardName = async () => {
    if (!conversation?.id || conversation.pending) {
      setRenamingBoard(false);
      return;
    }
    const trimmed = boardNameDraft.trim();
    if (!trimmed) {
      toast({
        title: 'Board name required',
        description: 'Enter a name for the Expert Board.',
        variant: 'destructive',
      });
      return;
    }
    if (trimmed === (boardName ?? '').trim()) {
      setRenamingBoard(false);
      return;
    }

    const order = uniqueConversationMethodIds(
      selectedLensIds.length > 0
        ? selectedLensIds
        : [conversation.thinking_method_id ?? thinkingMethod?.id],
    );
    if (order.length < 2) {
      setRenamingBoard(false);
      return;
    }

    setSavingBoardName(true);
    try {
      const nextPayload = buildConversationMethodsPayload(order, { name: trimmed });
      const { data, error } = await supabase
        .from('conversations')
        .update({ openai_conversation_id: nextPayload })
        .eq('id', conversation.id)
        .select('openai_conversation_id, updated_at')
        .single();
      if (error) throw error;

      const updated: Conversation = {
        ...conversation,
        openai_conversation_id: data?.openai_conversation_id ?? nextPayload,
      };
      setConversation(updated);
      conversationRef.current = updated;
      setRenamingBoard(false);
      toast({ title: 'Board renamed' });
    } catch (error) {
      console.error('Error renaming Expert Board:', error);
      playErrorSound();
      toast({
        title: 'Error',
        description: 'Failed to rename Board',
        variant: 'destructive',
      });
    } finally {
      setSavingBoardName(false);
    }
  };

  return (
    <div className={`${boardMode ? 'bg-[hsl(var(--brand-accent-warm-bg))]/30 text-foreground' : 'bg-background'} flex h-screen h-[100dvh] w-full min-w-0 flex-col overflow-x-hidden`}>
      {/* Header */}
      <Header />
      
      {/* Conversation Header */}
      <div className={`${boardMode ? 'border-b border-[hsl(var(--brand-accent-warm-text))]/20 bg-card/95 shadow-sm' : 'border-b border-border/30 bg-background/80'} min-w-0 backdrop-blur-sm z-40`}>
         <div className={`${isArtifactPanelActive ? 'max-w-full' : boardMode ? 'max-w-6xl mx-auto' : 'max-w-4xl mx-auto'} px-3 sm:px-4 ${boardMode ? 'h-14 sm:h-[72px]' : 'h-14'} flex items-center justify-between gap-2`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(fromApplications ? '/applications' : boardMode ? '/expert-boards' : `/lens/${thinkingMethod.id}`)}
            className={`${boardMode ? 'text-foreground hover:bg-[hsl(var(--brand-accent-warm-bg))] hover:text-foreground' : 'hover:bg-muted/50'} px-2 sm:px-3 flex-shrink-0`}
          >
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{fromApplications ? 'Apps' : boardMode ? 'Boards' : 'Back'}</span>
          </Button>
          
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1 justify-end">
            {boardMode ? (
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                <div className="flex shrink-0 -space-x-2">
                  {selectedLensDetails.slice(0, 3).map((lens) => (
                    <Avatar key={lens.id} className="h-7 w-7 sm:h-10 sm:w-10 border-2 border-[hsl(var(--brand-accent-warm-text))]/40 shadow-sm">
                      <AvatarImage src={lens.icon_url} alt={lens.author} />
                      <AvatarFallback className="bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-[10px] font-semibold">
                        {lens.author.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  {renamingBoard ? (
                    <div className="flex min-w-0 items-center gap-1.5">
                      <Input
                        value={boardNameDraft}
                        onChange={(event) => setBoardNameDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void saveBoardName();
                          } else if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelRenamingBoard();
                          }
                        }}
                        autoFocus
                        maxLength={120}
                        disabled={savingBoardName}
                        className="h-7 min-w-0 w-full max-w-[180px] sm:max-w-[260px] px-2 py-1 text-sm font-semibold"
                        placeholder="Board name"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => void saveBoardName()}
                        disabled={savingBoardName}
                        aria-label="Save Board name"
                      >
                        {savingBoardName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={cancelRenamingBoard}
                        disabled={savingBoardName}
                        aria-label="Cancel rename"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={startRenamingBoard}
                      disabled={!conversation || conversation.pending}
                      className="group/board-name flex min-w-0 max-w-full items-center gap-1.5 text-left disabled:cursor-default"
                      title="Rename Board"
                    >
                      <h1 className="min-w-0 truncate text-sm sm:text-base font-semibold text-foreground">
                        {boardName || 'Untitled Board'}
                      </h1>
                      <Pencil className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover/board-name:opacity-100" />
                    </button>
                  )}
                  <p className="truncate text-[11px] sm:text-xs text-muted-foreground">
                    {selectedLensDetails.length} {selectedLensDetails.length === 1 ? 'Expert' : 'Experts'}
                  </p>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3 hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/lens/${thinkingMethod.id}`)}
              >
                <div className="text-right min-w-0">
                  <h1 className="text-sm font-medium text-foreground truncate max-w-[140px] sm:max-w-[220px]">{thinkingMethod.name}</h1>
                  <p className="text-xs text-muted-foreground truncate">by {thinkingMethod.author}</p>
                </div>
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={thinkingMethod.icon_url} alt={thinkingMethod.name} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            )}

            <Popover
              open={lensPickerOpen}
              onOpenChange={(open) => {
                if (open) {
                  openExpertBoardPicker();
                } else {
                  setLensPickerOpen(false);
                  setSelectedLensIndustry(null);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={lensSwitchDisabled || groupedAvailableLenses.length === 0}
                  aria-label={boardMode ? 'Manage board experts' : 'Add experts'}
                  className={boardMode
                    ? 'h-10 w-10 shrink-0 gap-1.5 px-0 sm:h-9 sm:w-auto sm:px-3 border-[hsl(var(--brand-accent-warm-text))]/40 bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]/70'
                    : 'h-10 w-10 shrink-0 gap-1.5 px-0 sm:h-8 sm:w-auto sm:px-3 border-primary/40 text-primary hover:bg-primary/5 hover:text-primary'}
                  title={boardMode ? "Manage the Experts on this Expert Board" : "Add Experts and open this conversation as an Expert Board"}
                >
                  {switchingLens ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Brain className="h-3.5 w-3.5" />
                  )}
                  <span className="hidden sm:inline">{boardMode ? 'Board Members' : 'Switch to Expert Board Mode'}</span>
                  <ChevronDown className="hidden h-3.5 w-3.5 sm:block" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="center" className="w-[calc(100vw-2rem)] p-0 sm:w-96">
                {!boardMode && (() => {
                  const nameMissing = trimmedBoardCreationName.length === 0;
                  const nameIsBlocking = nameMissing && boardDraftLensIds.length >= 2;
                  return (
                    <div className="border-b border-border/50 px-3 py-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="chat-board-name-input" className="text-xs font-medium text-muted-foreground">
                          Board name <span className="text-destructive">*</span>
                        </Label>
                        {nameIsBlocking && (
                          <span className="text-[11px] font-medium text-destructive">Required to create</span>
                        )}
                      </div>
                      <Input
                        id="chat-board-name-input"
                        value={boardCreationNameDraft}
                        onChange={(event) => setBoardCreationNameDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && trimmedBoardCreationName.length > 0 && boardDraftLensIds.length >= 2) {
                            event.preventDefault();
                            saveExpertBoardMode();
                          }
                        }}
                        maxLength={120}
                        disabled={switchingLens}
                        placeholder="e.g. Q3 Pricing Review"
                        autoFocus
                        className={`mt-1.5 h-8 text-sm ${nameIsBlocking ? 'border-destructive ring-1 ring-destructive/40 focus-visible:ring-destructive' : ''}`}
                      />
                    </div>
                  );
                })()}
                <div className="border-b border-border/50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs font-medium text-muted-foreground">
                      {boardMode ? 'Expert order' : 'Expert Board Mode'}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{boardDraftLensIds.length}/{MAX_CONFERENCE_LENSES}</div>
                  </div>
                  <div className="space-y-1.5">
                    {boardDraftLensDetails.map((lens, index) => {
                      const firstLensLocked = !boardMode && index === 0;
                      const removeDisabled = switchingLens || firstLensLocked || (boardMode ? boardDraftLensDetails.length <= 2 : boardDraftLensDetails.length <= 1);
                      const moveUpDisabled = switchingLens || index === 0 || (!boardMode && index === 1);
                      const moveDownDisabled = switchingLens || index === boardDraftLensDetails.length - 1 || firstLensLocked;
                      return (
                      <div key={lens.id} className="flex items-center gap-2 rounded-md border border-border/50 px-2 py-1.5">
                        <span className="w-4 text-center text-[11px] text-muted-foreground">{index + 1}</span>
                        <Avatar className="h-6 w-6 flex-shrink-0">
                          <AvatarImage src={lens.icon_url} alt={lens.author} />
                          <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                            {lens.author.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">{lens.author}</div>
                          <div className="truncate text-[11px] text-muted-foreground">{lens.name}</div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={moveUpDisabled}
                          onClick={() => moveConferenceLens(lens.id, -1)}
                          title="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={moveDownDisabled}
                          onClick={() => moveConferenceLens(lens.id, 1)}
                          title="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 hover:text-destructive"
                          disabled={removeDisabled}
                          onClick={() => removeConferenceLens(lens.id)}
                          title={firstLensLocked ? 'The current Expert stays first' : removeDisabled ? 'Expert Boards need at least two Experts' : 'Remove Expert'}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                    })}
                  </div>
                </div>

                {boardDraftLensIds.length >= MAX_CONFERENCE_LENSES ? (
                  <div className="p-3 text-xs text-muted-foreground">
                    A Board is capped at {MAX_CONFERENCE_LENSES} Experts.
                  </div>
                ) : !selectedLensIndustry ? (
                  <div className="py-2">
                    <div className="px-3 pb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Plus className="h-3.5 w-3.5" />
                      Add Expert
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {groupedAvailableLenses.map(([industry, lenses]) => (
                        <button
                          key={industry}
                          type="button"
                          onClick={() => setSelectedLensIndustry(industry)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left text-sm hover:bg-muted transition-colors"
                        >
                          <span className="font-medium text-foreground truncate">{getIndustryMenuLabel(industry)}</span>
                          <span className="text-xs text-muted-foreground">
                            {lenses.filter((lens) => !boardDraftLensIdSet.has(lens.id)).length}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="py-2">
                    <button
                      type="button"
                      onClick={() => setSelectedLensIndustry(null)}
                      className="w-full flex items-center gap-2 px-3 pb-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      {getIndustryMenuLabel(selectedLensIndustry)}
                    </button>
                    <div className="max-h-64 overflow-y-auto">
                      {(groupedAvailableLenses.find(([industry]) => industry === selectedLensIndustry)?.[1] || []).map((lens) => {
                        const isSelectedLens = boardDraftLensIdSet.has(lens.id);
                        return (
                          <button
                            key={lens.id}
                            type="button"
                            disabled={isSelectedLens || switchingLens}
                            onClick={() => addConferenceLens(lens)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted transition-colors disabled:cursor-default disabled:opacity-60"
                          >
                            <Avatar className="h-7 w-7 flex-shrink-0">
                              <AvatarImage src={lens.icon_url} alt={lens.author} />
                              <AvatarFallback className="bg-primary/10 text-primary text-[11px]">
                                {lens.author.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-foreground">{lens.author}</span>
                              <span className="block truncate text-xs text-muted-foreground">{lens.name}</span>
                            </span>
                            {isSelectedLens ? <Check className="h-3.5 w-3.5 text-primary" /> : <Plus className="h-3.5 w-3.5 text-muted-foreground" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 border-t border-border/50 p-3">
                  {(() => {
                    const nameBlocking = !boardMode && trimmedBoardCreationName.length === 0;
                    const lensesBlocking = boardDraftLensIds.length < 2;
                    const message = nameBlocking
                      ? 'Enter a Board name above to create it.'
                      : lensesBlocking
                      ? 'Add one more Expert to create a Board.'
                      : 'Experts answer in this order.';
                    const isBlocking = nameBlocking || lensesBlocking;
                    return (
                      <p className={`flex items-center gap-1.5 text-xs font-medium ${isBlocking ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {isBlocking && <Info className="h-3.5 w-3.5 shrink-0" />}
                        {message}
                      </p>
                    );
                  })()}
                  <Button
                    size="sm"
                    onClick={saveExpertBoardMode}
                    disabled={switchingLens || boardDraftLensIds.length < 2 || (!boardMode && trimmedBoardCreationName.length === 0)}
                  >
                    {switchingLens && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    {boardMode ? 'Save Board' : 'Create Board'}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="flex items-center gap-1">
            {contextDocuments.length > 0 && (
              <Popover open={sourceDocumentsOpen} onOpenChange={setSourceDocumentsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={boardMode
                      ? 'h-9 gap-1.5 border-[hsl(var(--brand-accent-warm-text))]/40 bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]/70'
                      : 'h-8 gap-1.5 border-primary/40 text-primary hover:bg-primary/5 hover:text-primary'}
                    title="Attached source documents"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline text-xs font-medium">
                      {contextDocuments.length} {contextDocuments.length === 1 ? 'source' : 'sources'}
                    </span>
                    <span className="sm:hidden text-xs font-medium">{contextDocuments.length}</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0">
                  <div className="border-b border-border/50 px-3 py-2 text-xs font-medium text-muted-foreground">
                    Source documents ({contextDocuments.length})
                  </div>
                  <div className="max-h-72 overflow-y-auto p-2 space-y-1.5">
                    {contextDocuments.map((sourceDocument) => (
                      <div
                        key={sourceDocument.id}
                        className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${
                          sourceDocument.active && sourceDocument.status === 'ready'
                            ? 'border-primary/30 bg-primary/5'
                            : 'border-border/60 bg-muted/40 text-muted-foreground'
                        }`}
                      >
                        <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium" title={sourceDocument.file_name}>{sourceDocument.file_name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {formatContextDocumentSize(sourceDocument.file_size)}
                            {sourceDocument.status === 'uploading' || sourceDocument.replacementUploading ? (
                              <span className="ml-1">· {sourceDocument.replacementUploading ? 'Replacing…' : 'Uploading…'}</span>
                            ) : sourceDocument.status !== 'ready' && (
                              <span className="ml-1 text-destructive">
                                · {sourceDocument.error || sourceDocument.status}
                              </span>
                            )}
                          </div>
                        </div>
                        {sourceDocument.status === 'uploading' || sourceDocument.replacementUploading ? (
                          <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-primary" aria-label={`${sourceDocument.replacementUploading ? 'Replacing' : 'Uploading'} ${sourceDocument.file_name}`} />
                        ) : sourceDocument.status === 'ready' && (
                          <Switch
                            checked={sourceDocument.active}
                            onCheckedChange={(checked) => toggleContextDocumentActive(sourceDocument, checked)}
                            disabled={isInputDisabled}
                            aria-label={`Use ${sourceDocument.file_name} as source context`}
                            className="scale-75"
                          />
                        )}
                        {sourceDocument.status !== 'uploading' && !sourceDocument.replacementUploading && <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => deleteContextDocument(sourceDocument)}
                          aria-label={`Delete ${sourceDocument.file_name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>}
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={deleteConversation}
              disabled={deletingConversation}
              aria-label={boardMode ? 'Delete board' : 'Delete conversation'}
              title={boardMode ? 'Delete board' : 'Delete conversation'}
              className={boardMode ? 'h-10 w-10 shrink-0 p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive' : 'h-10 w-10 shrink-0 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10'}
            >
              {deletingConversation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {isArtifactPanelActive && (
        <div className="flex shrink-0 gap-1 border-b border-border/50 bg-background p-2 md:hidden" role="group" aria-label="Conversation panels">
          <button type="button" aria-pressed={mobilePanel === 'chat'} onClick={() => setMobilePanel('chat')}
            className={`min-h-11 min-w-0 flex-1 rounded-md px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${mobilePanel === 'chat' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            Chat
          </button>
          <button type="button" aria-pressed={mobilePanel === 'document'} onClick={() => setMobilePanel('document')}
            className={`min-h-11 min-w-0 flex-1 rounded-md px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${mobilePanel === 'document' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            Document
          </button>
        </div>
      )}

      {/* Main Content - Split Layout when artifact exists */}
      <div className={`flex min-w-0 flex-1 ${isArtifactPanelActive ? (boardMode ? 'md:divide-x md:divide-[hsl(var(--brand-accent-warm-text))]/15' : 'md:divide-x md:divide-border') : ''} min-h-0 ${boardMode ? 'bg-[hsl(var(--brand-accent-warm-bg))]/30' : ''}`}>
        {/* Left Panel - Chat with Input */}
        <div
          className={`${isArtifactPanelActive ? (mobilePanel === 'chat' ? 'flex md:w-1/2' : 'hidden md:flex md:w-1/2') : 'flex w-full'} min-w-0 flex-col min-h-0 ${boardMode ? 'bg-transparent' : ''}`}
          onDragOver={handleContextDocumentDragOver}
          onDrop={handleContextDocumentDrop}
        >
          {/* Messages Container - Scrollable */}
          <div
            ref={messagesContainerRef}
            className={`flex-1 overflow-y-auto min-h-0 ${boardMode ? 'bg-transparent' : ''}`}
            onScroll={handleMessagesScroll}
          >
            <div className={isArtifactPanelActive ? 'px-4' : boardMode ? 'max-w-4xl mx-auto' : 'max-w-3xl mx-auto'}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[60vh] px-4 animate-fade-in">
                  <Avatar className={`h-16 w-16 mb-4 ${boardMode ? 'border-2 border-[hsl(var(--brand-accent-warm-text))]/40 shadow-md' : ''}`}>
                    <AvatarImage src={thinkingMethod.icon_url} alt={thinkingMethod.name} />
                    <AvatarFallback className={boardMode ? 'bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-xl font-medium' : 'bg-primary/10 text-primary text-xl font-medium'}>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className={`text-xl font-medium mb-2 ${boardMode ? 'text-foreground' : 'text-foreground'}`}>
                    {thinkingMethod.name}
                  </h3>
                  <p className={`text-center max-w-md text-sm line-clamp-3 mb-3 ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                    {thinkingMethod.bio}
                  </p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={boardMode ? 'gap-2 text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]' : 'gap-2 text-primary hover:text-primary'}
                      >
                        <Info className="h-4 w-4" />
                        View Full Profile
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <div className="flex items-center gap-4 mb-4">
                          <Avatar className="h-16 w-16">
                            <AvatarImage src={thinkingMethod.icon_url} alt={thinkingMethod.name} />
                            <AvatarFallback className="bg-primary/10 text-primary text-xl font-medium">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <DialogTitle className="text-2xl">{thinkingMethod.name}</DialogTitle>
                            <DialogDescription className="text-base">
                              by {thinkingMethod.author}
                            </DialogDescription>
                          </div>
                        </div>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2 text-foreground">Biography</h4>
                          <p className="text-muted-foreground whitespace-pre-wrap">{thinkingMethod.bio}</p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : (
                <>
                  {activePath.map((message, index) => {
                const versionInfo = getVersionInfoForMessage(message);

                const rawContent = typeof message.content === 'string'
                  ? message.content
                  : JSON.stringify(message.content);
                const parsedAssistant = !message.is_user
                  ? extractNextMoveActionItems(rawContent)
                  : { content: rawContent, actionItems: [] as NextMoveActionItem[] };
                const assistantContent = parsedAssistant.content;
                const actionItems = parsedAssistant.actionItems;

                const messageLens = !message.is_user && message.thinking_method_id
                  ? lensById.get(message.thinking_method_id)
                  : null;
                const showBoardSenderHeader = boardMode && !message.is_user && !!messageLens;

                return (
                  <div
                    key={`${message.id}-${index}`}
                    onClick={() => {
                      if (message.kind !== "ARTF" && window.matchMedia('(min-width: 768px)').matches) {
                        setSelectedArtifactId(null);
                      }
                    }}
                    className={`group py-4 px-4 animate-fade-in`}
                  >
                    <div className={`${isArtifactPanelActive ? '' : boardMode ? 'max-w-4xl mx-auto' : 'max-w-3xl mx-auto'} flex gap-3 ${message.is_user ? 'justify-end' : 'justify-start'}`}>
                      {boardMode && !message.is_user && (
                        <Avatar className="mt-1 h-8 w-8 flex-shrink-0 border border-[hsl(var(--brand-accent-warm-text))]/30 shadow-sm">
                          <AvatarImage
                            src={messageLens?.icon_url || thinkingMethod.icon_url}
                            alt={messageLens?.author || thinkingMethod.author}
                          />
                          <AvatarFallback className="bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-[10px] font-semibold">
                            {(messageLens?.author || thinkingMethod.author)
                              .split(' ')
                              .map((word) => word[0])
                              .join('')
                              .toUpperCase()
                              .slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={`min-w-0 space-y-2 ${message.is_user
                        ? boardMode
                          ? 'max-w-[78%] rounded-2xl bg-primary px-4 py-3 text-primary-foreground shadow-sm'
                          : 'max-w-[80%] bg-primary/10 rounded-2xl px-4 py-3'
                        : boardMode
                          ? 'max-w-[86%] rounded-2xl border border-[hsl(var(--brand-accent-warm-text))]/25 bg-card px-4 py-3 text-foreground shadow-sm'
                          : 'max-w-[85%] bg-muted/50 rounded-2xl px-4 py-3'}`}>
                        {showBoardSenderHeader && (
                          <div className="-mt-1 mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm font-semibold text-foreground">{messageLens?.author}</span>
                            <span className="text-xs text-muted-foreground">{messageLens?.name}</span>
                          </div>
                        )}
                        {/* Message text */}
                        {message.is_user ? (
                          editingMessageId === message.id ? (
                            <div className="space-y-3">
                              {!editingArtifactId && (
                              <div className="flex items-center gap-2 mb-2">
                                <Switch
                                  id={`edit-deep-thinking-${message.id}`}
                                  checked={editDeepThinkingMode}
                                  onCheckedChange={(checked) => {
                                    if (checked && isAnonymous) {
                                      redirectAnonymousDocumentCreation();
                                      return;
                                    }
                                    if (checked && documentSwitchLocked) {
                                      promptDocumentUpgrade();
                                      return;
                                    }
                                    setEditDeepThinkingMode(checked);
                                  }}
                                  disabled={documentSwitchLocked && !editDeepThinkingMode}
                                  className="data-[state=checked]:bg-primary"
                                />
                                <Label
                                  htmlFor={`edit-deep-thinking-${message.id}`}
                                  className={`text-xs font-medium cursor-pointer flex items-center gap-1.5 ${boardMode ? 'text-primary-foreground' : 'text-foreground'}`}
                                >
                                  <Zap className={`h-3 w-3 ${editDeepThinkingMode ? (boardMode ? 'text-primary-foreground' : 'text-primary') : (boardMode ? 'text-primary-foreground/70' : 'text-muted-foreground')}`} />
                                  Create App’s Document
                                </Label>
                                {documentSwitchLocked && !editDeepThinkingMode && (
                                  <button
                                    type="button"
                                    onClick={promptDocumentUpgrade}
                                    className={`text-[11px] font-medium hover:underline ${boardMode ? 'text-primary-foreground/85' : 'text-primary'}`}
                                  >
                                    {documentLimitReached ? 'Limit reached' : 'Request credits'}
                                  </button>
                                )}
                              </div>
                              )}
                              <Textarea
                                ref={editTextareaRef}
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                onKeyDown={(e) => handleEditKeyPress(e, message.id)}
                                className={boardMode
                                  ? 'min-h-[60px] rounded-xl border-[hsl(var(--brand-accent-warm-text))]/25 bg-card text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-accent-warm-text))]/60 focus:ring-1 focus:ring-[hsl(var(--brand-accent-warm-text))]/25'
                                  : 'min-h-[60px] rounded-xl border-border bg-background text-foreground placeholder:text-muted-foreground'}
                                placeholder="Edit your message..."
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => saveEdit(message.id)}
                                  className={boardMode ? 'rounded-full bg-card text-foreground hover:bg-card/90' : 'rounded-full'}
                                  disabled={sending}
                                >
                                  {sending ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : (
                                    <Send className="h-3 w-3 mr-1" />
                                  )}
                                  Send
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEdit}
                                  className={boardMode ? 'rounded-full border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground' : 'rounded-full'}
                                  disabled={sending}
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className={`text-[15px] leading-7 whitespace-pre-wrap break-words ${boardMode ? 'text-primary-foreground' : 'text-foreground'}`}>
                              {message.content}
                            </div>
                          )
                        ) : (
                          <div className={`text-[15px] leading-7 ${boardMode ? 'text-foreground' : 'text-foreground'}`}>
                            {message.kind === "QUESTION" && (
                              <QuestionMessage message={message} />
                            )}
                            {message.kind === "ANSWER" && (
                              <div className="space-y-2">
                                <div className={`flex items-center gap-2 text-xs font-medium ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                                  <span>Your answer</span>
                                </div>
                                <div className={`rounded-lg border p-3 ${boardMode ? 'border-[hsl(var(--brand-accent-warm-text))]/25 bg-[hsl(var(--brand-accent-warm-bg))]/40' : 'border-border/50 bg-muted/20'}`}>
                                  <FormattedMessage content={message.content} />
                                </div>
                              </div>
                            )}
                            {message.kind === "ARTF" && (
                              <div className={`flex items-center gap-2 mb-3 text-xs font-medium ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-primary'}`}>
                                <Zap className="h-3 w-3" />
                                <span>Document</span>
                              </div>
                            )}
                            {message.kind === "ARTF" ? (
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedArtifactId(message.id);
                                  setMobilePanel('document');
                                }}
                                className={`rounded-lg border p-4 transition-colors text-left w-full ${
                                  selectedArtifact?.id === message.id
                                    ? boardMode
                                      ? 'border-[hsl(var(--brand-accent-warm-text))] bg-[hsl(var(--brand-accent-warm-bg))]'
                                      : 'border-primary bg-primary/5'
                                    : boardMode
                                      ? 'border-[hsl(var(--brand-accent-warm-text))]/30 bg-[hsl(var(--brand-accent-warm-bg))]/40 hover:border-[hsl(var(--brand-accent-warm-text))]/55 hover:bg-[hsl(var(--brand-accent-warm-bg))]/60'
                                      : 'border-border/50 hover:border-border hover:bg-muted/30'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 mt-1">
                                    <svg
                                      className={`w-5 h-5 ${selectedArtifact?.id === message.id ? 'text-primary' : 'text-muted-foreground'}`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className={`text-sm font-medium line-clamp-2 ${boardMode ? 'text-foreground' : 'text-foreground'}`}>
                                      {getArtifactTitle(message.content)}
                                    </div>
                                    <div className={`text-xs mt-1 ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>Document</div>
                                  </div>
                                </div>
                              </button>
                            ) : message.kind !== "QUESTION" && message.kind !== "ANSWER" ? (
                              <>
                                <FormattedMessage content={assistantContent} />
                                {actionItems.length > 0 && (
                                  <div className="mt-5 space-y-2">
                                    <div className={`text-sm font-semibold ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>Related</div>
                                    <div className={`rounded-xl border overflow-hidden divide-y ${boardMode ? 'border-[hsl(var(--brand-accent-warm-text))]/25 divide-[hsl(var(--brand-accent-warm-text))]/20 bg-[hsl(var(--brand-accent-warm-bg))]/30' : 'border-border/40 divide-border/40'}`}>
                                      {actionItems.map((item, actionIndex) => {
                                        const isGeneric = item.type === 'generic_question';
                                        const primaryText = isGeneric
                                          ? item.question || item.description || item.title || item.raw
                                          : item.title || item.description || item.raw;
                                        const secondaryText = isGeneric
                                          ? item.title && item.title !== primaryText
                                            ? item.title
                                            : undefined
                                          : item.description && item.description !== primaryText
                                            ? item.description
                                            : undefined;
                                        const metaLabel =
                                          item.type === 'generic_question'
                                            ? 'Generic Question'
                                            : item.type === 'create_artifact'
                                              ? 'Create App’s Document'
                                              : undefined;

                                        return (
                                          <button
                                            key={`${message.id}-action-${actionIndex}`}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleActionItemSelect(item);
                                            }}
                                            className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${boardMode ? 'hover:bg-[hsl(var(--brand-accent-warm-bg))]/60' : 'hover:bg-muted/30'}`}
                                          >
                                            <CornerDownRight className={`h-3.5 w-3.5 mt-1 ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-muted-foreground'}`} />
                                            <div className="flex-1 min-w-0">
                                              {metaLabel && (
                                                <div className={`text-[11px] uppercase tracking-wide mb-1 ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-muted-foreground'}`}>
                                                  {metaLabel}
                                                </div>
                                              )}
                                              <div className={`text-sm leading-snug ${boardMode ? 'text-foreground' : 'text-foreground'}`}>{primaryText}</div>
                                              {secondaryText && (
                                                <div className={`text-xs mt-1 ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>{secondaryText}</div>
                                              )}
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : null}
                          </div>
                        )}

                        <div className="flex items-center gap-3 pt-1">
                          {message.is_user && editingMessageId !== message.id && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEdit(message)}
                                className="h-7 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted"
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteMessage(message)}
                                disabled={deletingMessageId === message.id}
                                className={`h-7 px-2 text-xs transition-opacity hover:bg-destructive/10 hover:text-destructive ${
                                  deletingMessageId === message.id
                                    ? 'opacity-100'
                                    : 'opacity-0 group-hover:opacity-100'
                                }`}
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Delete
                              </Button>
                              <UserVersions
                                total={versionInfo.total}
                                currentIndex={versionInfo.currentIndex}
                                onPrevious={() => {
                                  const prev = versionInfo.siblings[versionInfo.currentIndex - 1];
                                  if (prev) {
                                    // Branch point is always the chain root (first version = siblings[0])
                                    const chainRootId = versionInfo.siblings[0].id;
                                    selectBranch(chainRootId, prev.id);
                                    setSelectedArtifactId(null);
                                  }
                                }}
                                onNext={() => {
                                  const next = versionInfo.siblings[versionInfo.currentIndex + 1];
                                  if (next) {
                                    const chainRootId = versionInfo.siblings[0].id;
                                    selectBranch(chainRootId, next.id);
                                    setSelectedArtifactId(null);
                                  }
                                }}
                              />
                            </>
                          )}
                          {!message.is_user && message.kind !== "ARTF" && message.kind !== "QUESTION" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyMessage(message);
                              }}
                              className="h-7 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted"
                              title="Copy message"
                            >
                              {copiedMessageId === message.id ? (
                                <>
                                  <Check className="h-3 w-3 mr-1" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </Button>
                          )}
                          {!message.is_user && message.parent_message_id && versionInfo.total > 1 && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <AssistantVersions
                                total={versionInfo.total}
                                currentIndex={versionInfo.currentIndex}
                                onPrevious={() => {
                                  const prev = versionInfo.siblings[versionInfo.currentIndex - 1];
                                  if (prev) {
                                    selectBranch(message.parent_message_id!, prev.id);
                                    setSelectedArtifactId(null);
                                  }
                                }}
                                onNext={() => {
                                  const next = versionInfo.siblings[versionInfo.currentIndex + 1];
                                  if (next) {
                                    selectBranch(message.parent_message_id!, next.id);
                                    setSelectedArtifactId(null);
                                  }
                                }}
                              />
                            </div>
                          )}
                          {/* Metadata - always visible for assistant messages */}
                          {!message.is_user && message.model_latency_ms && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{formatResponseTime(message.model_latency_ms)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {boardMode && message.is_user && (
                        <Avatar className="mt-1 h-8 w-8 flex-shrink-0 border border-primary/30 shadow-sm">
                          <AvatarImage src={userAvatarUrl} alt={userDisplayName} />
                          <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">
                            {userInitials || 'U'}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  </div>
                  );
                  })}
                  
                  {streamingResponse && (
                    <div className="py-4 px-4 animate-fade-in">
                      <div className={`${isArtifactPanelActive ? '' : boardMode ? 'max-w-4xl mx-auto' : 'max-w-3xl mx-auto'} flex justify-start gap-3`}>
                        {boardMode && (
                          <Avatar className="mt-1 h-8 w-8 flex-shrink-0 border border-[hsl(var(--brand-accent-warm-text))]/30 shadow-sm">
                            <AvatarImage src={thinkingMethod.icon_url} alt={thinkingMethod.author} />
                            <AvatarFallback className="bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-[10px] font-semibold">
                              {thinkingMethod.author.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`${boardMode ? 'max-w-[86%] rounded-2xl border border-[hsl(var(--brand-accent-warm-text))]/25 bg-card text-foreground shadow-sm' : 'max-w-[85%] bg-muted/50 rounded-2xl'} px-4 py-3 space-y-3`}>
                          <div className={`flex items-center gap-2 text-xs ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{waitingForFirstTokens ? 'Connecting to model...' : 'Streaming response...'}</span>
                            {streamingResponse.isDeepThinking && (
                              <span className={`flex items-center gap-1 ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-primary'}`}>
                                <Zap className="h-3 w-3" />
                                <span className="text-[11px] uppercase tracking-wide">Document</span>
                              </span>
                            )}
                          </div>

                          {(() => {
                            const reasoning = streamingResponse.reasoning?.trim() || '';
                            const output = streamingResponse.output?.trim() || '';
                            const conferenceResults = streamingResponse.conferenceResults ?? [];
                            const showConferenceResults = boardMode && conferenceResults.length > 0;
                            const showReasoning = reasoning && reasoning !== output;
                            const showOutput = !!output && !showConferenceResults;

                            if (showConferenceResults) {
                              return (
                                <div className="space-y-3">
                                  {conferenceResults.map((result) => {
                                    const resultLens = result.thinkingMethodId
                                      ? lensById.get(result.thinkingMethodId)
                                      : null;
                                    const lensAuthor = result.lensAuthor || resultLens?.author || `Expert ${result.index + 1}`;
                                    const lensName = result.lensName || resultLens?.name || '';
                                    const avatarText = (lensAuthor || lensName || 'EX')
                                      .split(' ')
                                      .map((word) => word[0])
                                      .join('')
                                      .toUpperCase()
                                      .slice(0, 2);

                                    return (
                                      <div
                                        key={`${result.thinkingMethodId || 'expert'}-${result.index}`}
                                        className={`rounded-lg border p-3 ${result.status === 'error'
                                          ? 'border-destructive/25 bg-destructive/5'
                                          : 'border-[hsl(var(--brand-accent-warm-text))]/25 bg-[hsl(var(--brand-accent-warm-bg))]/40'}`}
                                      >
                                        <div className="mb-2 flex items-start justify-between gap-3">
                                          <div className="flex min-w-0 items-center gap-2.5">
                                            <Avatar className="h-7 w-7 flex-shrink-0 border border-[hsl(var(--brand-accent-warm-text))]/25">
                                              <AvatarImage src={resultLens?.icon_url || undefined} alt={lensAuthor} />
                                              <AvatarFallback className="bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-[10px] font-semibold">
                                                {avatarText}
                                              </AvatarFallback>
                                            </Avatar>
                                            <div className="min-w-0">
                                              <div className="truncate text-sm font-semibold text-foreground">{lensAuthor}</div>
                                              {lensName && (
                                                <div className="truncate text-xs text-muted-foreground">{lensName}</div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex flex-shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                                            {result.status === 'thinking' ? (
                                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : result.status === 'error' ? (
                                              <X className="h-3.5 w-3.5 text-destructive" />
                                            ) : (
                                              <Check className="h-3.5 w-3.5 text-[hsl(var(--brand-accent-warm-text))]" />
                                            )}
                                            <span>{result.status === 'thinking' ? 'Thinking' : result.status === 'error' ? 'Skipped' : 'Done'}</span>
                                          </div>
                                        </div>

                                        {result.content.trim() ? (
                                          <div className="text-[15px] leading-7 text-foreground">
                                            <FormattedMessage content={result.content} />
                                          </div>
                                        ) : (
                                          <div className="text-sm text-muted-foreground">
                                            Waiting for this Expert's answer...
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }

                            return (
                              <>
                                {showReasoning && (
                                  <div className={`rounded-lg border p-3 space-y-2 ${boardMode ? 'border-[hsl(var(--brand-accent-warm-text))]/25 bg-[hsl(var(--brand-accent-warm-bg))]/40' : 'border-border/50 bg-background/60'}`}>
                                    <div className={`text-sm whitespace-pre-wrap break-words min-h-[24px] ${boardMode ? 'text-foreground' : 'text-foreground'}`}>
                                      {reasoning}
                                    </div>
                                  </div>
                                )}
                                {showOutput && (
                                  <div className="space-y-2">
                                    <div className={`text-[15px] leading-7 ${boardMode ? 'text-foreground' : 'text-foreground'}`}>
                                      <FormattedMessage content={output} />
                                    </div>
                                  </div>
                                )}
                                {!showReasoning && !showOutput && (
                                  <div className={`rounded-lg border p-3 space-y-2 ${boardMode ? 'border-[hsl(var(--brand-accent-warm-text))]/25 bg-[hsl(var(--brand-accent-warm-bg))]/40' : 'border-border/50 bg-background/60'}`}>
                                    <div className={`text-sm whitespace-pre-wrap break-words min-h-[24px] ${boardMode ? 'text-foreground' : 'text-foreground'}`}>
                                      Thinking...
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Loading states */}
                  {showLoadingState && (
                    <div className="py-4 px-4 animate-fade-in">
                      <div className={`${isArtifactPanelActive ? '' : boardMode ? 'max-w-4xl mx-auto' : 'max-w-3xl mx-auto'} flex justify-start gap-3`}>
                        {boardMode && (
                          <Avatar className="mt-1 h-8 w-8 flex-shrink-0 border border-[hsl(var(--brand-accent-warm-text))]/30 shadow-sm">
                            <AvatarImage src={thinkingMethod.icon_url} alt={thinkingMethod.author} />
                            <AvatarFallback className="bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-[10px] font-semibold">
                              {thinkingMethod.author.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`${boardMode ? 'max-w-[86%] rounded-2xl border border-[hsl(var(--brand-accent-warm-text))]/25 bg-card text-muted-foreground shadow-sm' : 'max-w-[85%] bg-muted/50 rounded-2xl text-muted-foreground'} px-4 py-3 flex items-center gap-2 text-sm`}>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{loadingLabel}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Footer - Pinned to Left Panel */}
          <div className={boardMode ? 'border-t border-[hsl(var(--brand-accent-warm-text))]/20 bg-card' : 'border-t border-border/30 bg-background'}>
            <div className={`${isArtifactPanelActive ? 'px-3 sm:px-4' : boardMode ? 'max-w-4xl mx-auto px-3 sm:px-4' : 'max-w-3xl mx-auto px-3 sm:px-4'} py-3 md:py-4`}>
              {editingArtifact && (
                <div className={`mb-3 p-2.5 rounded-lg border flex items-center justify-between ${boardMode ? 'bg-[hsl(var(--brand-accent-warm-bg))] border-[hsl(var(--brand-accent-warm-text))]/30' : 'bg-secondary/50 border-border/50'}`}>
                  <div className="flex items-center gap-2.5">
                    <Pencil className={`h-3.5 w-3.5 ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-foreground'}`} />
                    <div className="flex flex-col">
                      <span className={`text-xs font-medium ${boardMode ? 'text-foreground' : 'text-foreground'}`}>Editing document</span>
                      <span className={`text-[11px] truncate max-w-[200px] ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                        {getArtifactTitle(editingArtifact.content)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelEditingArtifact}
                    className="h-6 w-6 p-0 hover:bg-muted"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              {/* Source documents moved to header (paperclip popover) */}
              {/* {investigationInProgress && (
                <div className="mb-3 p-2 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs text-primary font-medium">Answer questions</span>
                </div>
              )} */}
              <FirstMessageNotice hidden={messages.some((m) => m.is_user)} />
              <div className="relative min-w-0">
                {!editingArtifactId && (
                <div className={`mb-3 flex min-w-0 flex-wrap items-center gap-2 rounded-lg border px-2 py-2 backdrop-blur-sm md:absolute md:-top-10 md:right-0 md:mb-0 md:rounded-full md:px-3 md:py-1.5 ${
                  boardMode
                    ? investigationInProgress && useDeepThinkingMode
                      ? 'bg-[hsl(var(--brand-accent-warm-bg))] border-[hsl(var(--brand-accent-warm-text))]/40'
                      : 'bg-card/95 border-[hsl(var(--brand-accent-warm-text))]/25'
                    : investigationInProgress && useDeepThinkingMode
                      ? 'bg-primary/10 border-primary/30'
                      : 'bg-muted/80 border-border/50'
                }`}>
                  {investigationInProgress && useDeepThinkingMode ? (
                    <Lock className={`h-3.5 w-3.5 ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-primary'}`} />
                  ) : (
                    <Zap className={`h-3.5 w-3.5 ${useDeepThinkingMode ? (boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-primary') : (boardMode ? 'text-muted-foreground' : 'text-muted-foreground')}`} />
                  )}
                  <span className={`text-xs ${investigationInProgress && useDeepThinkingMode ? (boardMode ? 'text-[hsl(var(--brand-accent-warm-text))] font-medium' : 'text-primary font-medium') : (boardMode ? 'text-muted-foreground' : 'text-muted-foreground')}`}>
                    {investigationInProgress && useDeepThinkingMode ? 'Document Mode (locked)' : 'Create App’s Document'}
                  </span>
                  {selectedLensDetails.length > 1 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={isInputDisabled}
                          className={boardMode ? 'h-6 max-w-[150px] gap-1 px-2 text-[11px] text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]' : 'h-6 max-w-[150px] gap-1 px-2 text-[11px]'}
                        >
                          <Brain className="h-3 w-3" />
                          <span className="truncate">{activeDocumentLens?.name || 'Lens'}</span>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        {selectedLensDetails.map((lens) => (
                          <DropdownMenuItem
                            key={lens.id}
                            onClick={() => setDocumentLensId(lens.id)}
                            className="gap-2"
                          >
                            <Brain className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="min-w-0 flex-1 truncate">{lens.name}</span>
                            {activeDocumentLensId === lens.id && <Check className="h-3.5 w-3.5 text-primary" />}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Switch
                    id="deep-thinking"
                    aria-label="Create App’s Document mode"
                    checked={useDeepThinkingMode}
                    onCheckedChange={(checked) => {
                      if (checked && isAnonymous) {
                        redirectAnonymousDocumentCreation();
                        return;
                      }
                      if (checked && documentSwitchLocked) {
                        promptDocumentUpgrade();
                        return;
                      }
                      setUseDeepThinkingMode(checked);
                      // If user unlocks during investigation, clear investigation state
                      if (!checked && investigationInProgress) {
                        setInvestigationInProgress(false);
                        setInvestigationQuestionId(null);
                      }
                    }}
                    disabled={isInputDisabled || (documentSwitchLocked && !useDeepThinkingMode)}
                    title={
                      documentSwitchLocked && !useDeepThinkingMode
                        ? (documentLimitReached
                            ? 'Document limit reached'
                            : `Needs at least ${DOCUMENT_CREDIT_THRESHOLD} credits to unlock Document mode`)
                        : investigationInProgress && useDeepThinkingMode
                          ? "Turn off to send a regular chat message instead"
                          : undefined
                    }
                  />
                  {documentSwitchLocked && !useDeepThinkingMode && (
                    <button
                      type="button"
                      onClick={promptDocumentUpgrade}
                      className={`ml-1 text-[11px] font-medium hover:underline ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-primary'}`}
                    >
                      {documentLimitReached ? 'Limit reached' : 'Request credits'}
                    </button>
                  )}
                </div>
                )}
                <div className="flex min-w-0 items-end gap-2 sm:gap-3">
                <input
                  ref={contextDocumentInputRef}
                  type="file"
                  multiple
                  accept={CONTEXT_DOCUMENT_ACCEPT}
                  className="hidden"
                  onChange={handleContextDocumentSelected}
                />
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={boardMode ? 'h-10 w-10 flex-shrink-0 rounded-full border-[hsl(var(--brand-accent-warm-text))]/35 bg-card text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]' : 'h-10 w-10 flex-shrink-0 rounded-full'}
                        onClick={handleContextDocumentUploadClick}
                        disabled={isInputDisabled || contextDocumentUploading}
                        aria-label="Upload source document"
                      >
                        {contextDocumentUploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Paperclip className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Upload source document</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div className="relative min-w-0 flex-1">
                  <Textarea
                    ref={textareaRef}
                    placeholder={editingArtifact ? "Describe how to edit this document..." : "Message..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isInputDisabled}
                    className={boardMode
                      ? 'resize-none rounded-2xl border-[hsl(var(--brand-accent-warm-text))]/30 bg-card pr-12 py-3 px-4 min-h-[52px] max-h-[200px] text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-accent-warm-text))]/60 focus:ring-1 focus:ring-[hsl(var(--brand-accent-warm-text))]/25'
                      : 'resize-none rounded-2xl border-border/50 bg-muted/30 pr-12 py-3 px-4 min-h-[52px] max-h-[200px] focus:border-border focus:ring-1 focus:ring-primary/20'}
                    rows={1}
                  />
                  <Button 
                    onClick={showCancelButton ? handleCancelRequest : () => sendMessage(useDeepThinkingMode)} 
                    disabled={
                      showCancelButton ? !canCancelRequest : !newMessage.trim() || isInputDisabled
                    }
                    aria-label={showCancelButton ? "Cancel request" : "Send message"}
                    size="icon"
                    variant={showCancelButton ? "secondary" : "default"}
                    className={boardMode
                      ? `absolute right-1 bottom-1 h-10 w-10 rounded-full md:right-2 md:bottom-2 md:h-8 md:w-8 ${showCancelButton ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80' : 'bg-[hsl(var(--brand-accent-warm-text))] text-white hover:bg-[hsl(var(--brand-accent-warm-text))]/90'}`
                      : 'absolute right-1 bottom-1 h-10 w-10 rounded-full md:right-2 md:bottom-2 md:h-8 md:w-8'}
                  >
                    {showCancelButton ? (
                      <Square className="h-4 w-4" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                </div>
              </div>
              <p className={`mt-2 text-center text-[11px] ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                By using Applyt you agree to our{' '}
                <a href="/terms" className={boardMode ? 'underline hover:text-foreground' : 'underline hover:text-foreground'}>Terms</a> and{' '}
                <a href="/privacy" className={boardMode ? 'underline hover:text-foreground' : 'underline hover:text-foreground'}>Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>

        {/* Artifact Panel - Right Side */}
        {isArtifactPanelActive && (
          <div className={`${mobilePanel === 'document' ? 'flex' : 'hidden'} w-full min-w-0 flex-col min-h-0 md:flex md:w-1/2 ${boardMode ? 'bg-card' : 'bg-background'}`}>
            {/* Artifact Header */}
            <div className={`${boardMode ? 'border-b border-[hsl(var(--brand-accent-warm-text))]/20 bg-card' : 'border-b border-border/30 bg-background'} z-30 flex-shrink-0`}>
              <div className="flex min-w-0 flex-col gap-2 px-3 py-3 sm:px-4 md:min-h-14 md:flex-row md:items-center md:justify-between md:gap-3 md:px-6 md:py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className={`h-4 w-4 shrink-0 ${boardMode ? 'text-[hsl(var(--brand-accent-warm-text))]' : 'text-muted-foreground'}`} />
                  <span className={`min-w-0 truncate text-sm font-medium ${boardMode ? 'text-foreground' : ''}`} title={activeDocumentInfo?.title || getArtifactTitle(selectedArtifact?.content || '')}>
                    {activeDocumentInfo?.title || getArtifactTitle(selectedArtifact?.content || '')}
                  </span>
                  {activeDocumentInfo?.fileSize && (
                    <span className={`hidden shrink-0 text-xs lg:inline ${boardMode ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                      ({(activeDocumentInfo.fileSize / 1024).toFixed(1)} KB)
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedArtifactId(null)}
                    aria-label="Close document"
                    className="ml-auto h-10 w-10 shrink-0 md:hidden"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-2 md:justify-end">
                  <Button
                    variant={editingArtifactId === selectedArtifactId ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (editingArtifactId === selectedArtifactId) {
                        cancelEditingArtifact();
                      } else if (documentSwitchLocked) {
                        promptDocumentUpgrade();
                      } else {
                        startEditingArtifact(selectedArtifactId!);
                      }
                    }}
                    className={boardMode ? 'min-h-10 md:min-h-0 text-xs gap-1.5 border-[hsl(var(--brand-accent-warm-text))]/35 bg-card text-[hsl(var(--brand-accent-warm-text))] hover:bg-[hsl(var(--brand-accent-warm-bg))]' : 'min-h-10 md:min-h-0 text-xs gap-1.5'}
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </Button>
                  {selectedArtifact && (
                    <ArtifactOperatingSystemAction
                      selectedArtifact={selectedArtifact}
                      allArtifacts={allArtifacts}
                      messages={messages}
                      conversationId={conversation?.id}
                      artifactTitle={activeDocumentInfo?.title || getArtifactTitle(selectedArtifact.content)}
                      boardMode={boardMode}
                      isAnonymous={isAnonymous}
                      onAuthBlocked={() => openConvertPrompt("Sign in or create an account to build an App from this document.", "Create an App")}
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyArtifactToClipboard}
                    className={boardMode ? 'min-h-10 md:min-h-0 text-xs text-foreground hover:bg-[hsl(var(--brand-accent-warm-bg))]' : 'min-h-10 md:min-h-0 text-xs hover:bg-muted'}
                  >
                    Copy
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="default"
                        size="sm"
                        className={boardMode ? 'min-h-10 md:min-h-0 text-xs gap-1 bg-[hsl(var(--brand-accent-warm-text))] text-white hover:bg-[hsl(var(--brand-accent-warm-text))]/90' : 'min-h-10 md:min-h-0 text-xs gap-1'}
                      >
                        <Download className="h-3 w-3" />
                        Download
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={downloadAsMarkdown}>
                        <FileText className="h-4 w-4 mr-2" />
                        Download as MD
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={downloadAsPDF} disabled={pdfPreparing || !documentPdf?.pdfUrl}>
                        <FileText className="h-4 w-4 mr-2" />
                        {pdfPreparing ? 'Generating Document...' : 'Download as PDF'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            
            {/* Artifact Content - PDF Viewer */}
            <div ref={artifactScrollRef} className={`min-w-0 flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${boardMode ? 'bg-card' : ''}`}>
              <div data-pdf-viewer className="min-w-0 w-full">
                <PdfDocumentViewer
                  messageId={selectedArtifact.id}
                  userId={user?.id}
                  title={getArtifactTitle(selectedArtifact.content)}
                  onDocumentLoad={handleDocumentLoad}
                  exportBlocked={isAnonymous}
                  onExportBlocked={() => openConvertPrompt()}
                />
                {isAnonymous && (
                  <div className="px-4 py-2 text-center text-xs text-muted-foreground border-t border-border/30 bg-muted/20">
                    Preview only.{' '}
                    <button
                      type="button"
                      onClick={() => openConvertPrompt()}
                      className="font-medium text-primary hover:underline"
                    >
                      Sign in
                    </button>{' '}
                    to download or copy this document.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ConvertPrompt open={convertPromptOpen} onOpenChange={setConvertPromptOpen} reason={convertPromptReason} title={convertPromptTitle} />
    </div>
  );
};

export default Chat;
